import type { ComponentChildren } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { useCallback } from "preact/hooks";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useChat } from "@ai-sdk/react";
import {
  DefaultChatTransport,
  lastAssistantMessageIsCompleteWithToolCalls,
  type UIMessage,
} from "ai";
import { CalendarDays, Check, CircleAlert, CircleCheck, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Calendar } from "../ui/calendar";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { OUTLINE_BRAND_BTN_CLASS, PRIMARY_BRAND_BTN_CLASS } from "./constants";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { Button } from "../ui/button";
import { MessageInput } from "../ui/message-input";
import { TypingIndicator } from "../ui/typing-indicator";
import {
  applySizeUpBundleStayAiSubscription,
  getCustomerIdentifier,
  previewSubscriptionShipping,
  submitPartialCancelRequest,
  submitSwapRequest,
  swapOfferQueryOptions,
  swapStatusQueryKey,
  type CancelChatOfferContext,
  type StayAiIntervalUnit,
} from "@/lib/api";
import { toastSubscriptionApiError } from "./subscription-errors";
import { usePurchasedProductDetail } from "@/hooks/usePurchasedProductDetail";
import {
  computeSizeUpBundle,
  subscriptionPromoPercent,
  type SizeUpBundle,
} from "./cancel-chat-bundle";
import { trackBuyAgainWidgetInteract } from "@/lib/tracking";
import {
  cancelFlowTrackingProps,
  createCancelAttemptId,
} from "./cancel-attempt-id";
import { isCancelChatTestMode } from "./cancel-chat-test-mode";
import {
  buildCancelChatContext,
  clampProposedDelayWeeksList,
  clampProposedFrequencyMultipliersList,
  detectExplicitRetentionAsk,
  getEligibleOffersForReason,
  hasSwitchBeenShown,
  isPreSecondSwitchStillOfferable,
  resolveAvailableOffersForSubReason,
  resolveOffersAfterSwitchShown,
  shouldExhaustOfferOnDecline,
  type EligibleOffer,
  type ExplicitRetentionAsk,
} from "./cancel-chat-context";
import {
  buildBundleCardCopy,
  buildDelayChoices,
  buildFrequencyChoices,
  canOfferChangeFrequency,
  normalizeFrequencyToMonths,
  SWITCH_TO_ONE_TIME_BODY,
  SWITCH_TO_ONE_TIME_FIRST_TITLE,
  SWITCH_TO_ONE_TIME_FOOTER,
  SWITCH_TO_ONE_TIME_TITLE,
} from "./cancel-chat-cards";
import {
  getCancelReasonLabel,
  type CancelSubscriptionReasonId,
} from "./cancel-reasons";
import { type CancelAction } from "./CancelSubscriptionDialog";
import { ProductSwapOffer, type SwapCandidate } from "./ProductSwapOffer";
import { BundleOffer } from "./BundleOffer";
import { buildCancelGreeting } from "./cancel-chat-greeting";
import {
  CANCEL_EVERYTHING,
  WHICH_PRODUCT_QUESTION,
  buildProductChoices,
  partialConfirmQuestion,
  partialSuccessCopy,
} from "./cancel-chat-scope";
import {
  applyKeyboardGeometry,
  readKeyboardViewport,
} from "./keyboard-geometry";
import { conversationKey, shouldResetConversation } from "./cancel-chat-resume";
import {
  replyOptionClassName,
  SENT_BUBBLE_FILL,
  SENT_BUBBLE_TEXT,
} from "./chat-bubble-styles";
import { addBillingInterval } from "./pause-dates";
import {
  motivationEventProps,
  type MotivationAnswer,
  type MotivationSource,
} from "./cancel-chat-intake";
import { getMotivationChips } from "./cancel-chat-motivations";
import { createReplyOptionsPart } from "./cancel-chat-reply-options";
import {
  createOfferDetailsPart,
  serializeOfferProducts,
} from "./cancel-chat-offer-details";
// AI-composed swap picks (validateSwapSelection) are temporarily disabled —
// the card always uses the API's deterministic default bundle. Re-enable later:
// import { DEFAULT_SWAP_OFFER_CONFIG, validateSwapSelection } from "shared/swap-offer";

import { buildCancelSubject } from "shared/cancel-subject";
import {
  isCancelSubReasonId,
  type CancelSubReasonId,
} from "shared/cancel-sub-reason";
import { formatPrice, formatSubscriptionDateForDisplay } from "./formatters";
import {
  CANCEL_RIGHT_AFTER_LINE,
  OR_DIVIDER,
  cancelTodayLabel,
  deliverySavingLine,
  totalSavingLine,
  noFeeLine,
  saveLabel,
  scheduledMessage,
  scheduledTitle,
  waitHeading,
} from "./cancel-after-delivery-copy";
import {
  contractPerDeliveryTotal,
  lineTotals,
} from "./subscription-pricing";
import type { ManageSubscriptionSheetProduct } from "./types";

const API_BASE =
  import.meta.env.VITE_PUBLIC_SOCIAL_OCEAN_BASE_URL || "https://socialocean.co";

export interface CancelSubmitData {
  whyBought: string;
  /** The specific cancel driver in the customer's own words (chat intake). */
  whyDetail: string;
  cancelReason: string;
  cancelReasonOther?: string;
  cancelReasonId: CancelSubscriptionReasonId;
  cancelAction: CancelAction;
  offerDeclined: boolean;
  conversationId?: string;
  /** The fee the chat card showed (cancel_after_next_delivery only). */
  feeAmountWaived?: number | null;
}

export interface CancelRetentionMeta {
  whyBought: string;
  /** The specific cancel driver in the customer's own words (chat intake). */
  whyDetail: string;
  cancelReason: string;
  cancelReasonOther?: string;
  cancelReasonId: CancelSubscriptionReasonId;
  conversationId?: string;
}

export interface CancelSubscriptionChatProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: ManageSubscriptionSheetProduct;
  isBeforeSecondShipment: boolean;
  isFlexibleSubscription: boolean;
  changeFrequencyAvailable: boolean;
  trackingShop?: string | null;
  customerEmail?: string | null;
  customerName?: string | null;
  /** Titles of the OTHER products on this subscription contract (model context). */
  siblingProductTitles?: string[];
  /** Every product line on this subscription contract (including `product`). */
  contractProducts?: ManageSubscriptionSheetProduct[];
  onSubmit: (data: CancelSubmitData) => Promise<unknown> | void;
  /** Apply a delay directly (no modal) — the customer picks a preset in-chat. */
  onDelayDate?: (
    nextBillingDate: string,
    weeks: number,
    cancelContext?: CancelChatOfferContext,
  ) => Promise<unknown> | void;
  /** Change frequency directly (no modal) — the customer picks a cadence in-chat. */
  onChangeFrequencyTo?: (
    interval: number,
    intervalUnit: StayAiIntervalUnit,
    cancelContext?: CancelChatOfferContext,
  ) => Promise<unknown> | void;
  /** Fired on a fatal chat transport/stream error — parent falls back to the dialog. */
  onFatalError?: () => void;
  /** Cancel-after-next-delivery switch; off ⇒ the fee card is today's. */
  cancelAfterDeliveryEnabled?: boolean;
}

/** A retention lever the AI presented, plus the confirm-cancel card. */
type CardKind = EligibleOffer | "confirm";

interface OfferCard {
  /** The model tool-call id (or a synthetic id for the "just cancel" escape). */
  id: string;
  kind: CardKind;
  status: "pending" | "accepted" | "declined";
  /** The customer's recorded answer, shown as a sent bubble once resolved. */
  answer?: string;
  /** True for the escape-hatch confirm card (no model tool call to report). */
  synthetic?: boolean;
  /** AI-authored lead-in for the plain-text offers (delay / change_frequency). */
  message?: string;
  /**
   * Model-authored option sets, already clamped to the widget's bounds
   * (cancel-chat-context.ts). Null/absent → the card shows the standard presets.
   */
  proposedWeeks?: number[];
  proposedMultipliers?: number[];
  /**
   * The swap card's bundle. Currently always the API's deterministic default
   * (AI productIds composition is temporarily disabled). Absent on non-swap
   * cards; a synthetic watchdog swap card falls back to the default at render.
   */
  bundle?: { products: SwapCandidate[]; comboTotal: number | null };
  /** Second presentation of the same lever (terms-adjusted re-pitch). */
  rePitch?: boolean;
}

type SuccessKind =
  | "cancelled"
  /** ONE line removed; the rest of the contract keeps shipping. */
  | "partial_cancelled"
  | "swap"
  | "switched"
  | "delayed"
  | "frequency_changed"
  | "bundle"
  /** Cancel scheduled right after the next delivery. */
  | "scheduled";

interface ReplyOption {
  label: string;
  /**
   * Second, smaller line inside the button (e.g. the money). PRESENTATIONAL
   * ONLY — the transcript bubble, the analytics label, and the tool result all
   * use `label` alone, so adding a sublabel cannot move a number into the
   * conversation record.
   */
  sublabel?: string;
  onClick: () => void;
  primary?: boolean;
  pending?: boolean;
}

function useIsDesktop(breakpoint = 768): boolean {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" && window.innerWidth >= breakpoint,
  );
  useEffect(() => {
    const fn = () => setIsDesktop(window.innerWidth >= breakpoint);
    fn();
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [breakpoint]);
  return isDesktop;
}

/**
 * The visible viewport rectangle while the on-screen keyboard is open, via the
 * visualViewport API. The mobile sheet is position:fixed to the LAYOUT
 * viewport, which neither iOS nor Android shrinks for the keyboard (dvh doesn't
 * move either). When the keyboard is up we pin the sheet to EXACTLY the visible
 * rectangle (top = offsetTop, height = visible height) so the header and the
 * first message sit at the top of what the customer can see and the composer at
 * the bottom — never taller than the visible area, so nothing scrolls off the
 * top out of reach.
 *
 * Two things this has been burned by, both of which look like "the sheet just
 * doesn't shrink for the keyboard", and neither of which is about the geometry:
 *
 * 1. WHETHER we conclude the keyboard is open at all. The inset must NOT subtract
 *    vv.offsetTop — the body is scroll-locked under this modal, so iOS reveals the
 *    composer by panning the VISUAL viewport instead, and that subtraction made the
 *    inset vanish exactly when the keyboard was fully up. See readKeyboardViewport.
 * 2. WHETHER the numbers survive the cascade. They are applied imperatively with
 *    !important priority, because this package's Tailwind marks every utility
 *    !important and a plain inline style loses to it. See applyKeyboardGeometry.
 *
 * A single reading is also not enough: iOS fires `resize` when the keyboard STARTS
 * animating, when vv.height is often still the pre-keyboard height, and fires
 * nothing once it settles. So we re-measure every frame for a short window after
 * anything that could move the keyboard, and commit only when a number changes.
 */
function useKeyboardViewport(active: boolean): {
  keyboardOpen: boolean;
  offsetTop: number;
  visibleHeight: number | null;
} {
  const [state, setState] = useState<{
    keyboardOpen: boolean;
    offsetTop: number;
    visibleHeight: number | null;
  }>({ keyboardOpen: false, offsetTop: 0, visibleHeight: null });
  useEffect(() => {
    if (!active || typeof window === "undefined") return;
    const vv = window.visualViewport;
    if (!vv) return;
    // Pin page scroll to where it was: the browser's focus-reveal scroll (and
    // scroll chaining from the sheet) otherwise drags the page, and the sheet
    // — anchored to offsetTop — would chase it. Pinning keeps both steady.
    let pinnedScrollY: number | null = null;
    let last: {
      keyboardOpen: boolean;
      offsetTop: number;
      visibleHeight: number | null;
    } = { keyboardOpen: false, offsetTop: 0, visibleHeight: null };
    const update = () => {
      const next = readKeyboardViewport({
        layoutHeight: window.innerHeight,
        visualHeight: vv.height,
        visualOffsetTop: vv.offsetTop,
      });
      const { keyboardOpen } = next;
      if (keyboardOpen && pinnedScrollY == null) pinnedScrollY = window.scrollY;
      if (!keyboardOpen) pinnedScrollY = null;
      if (
        keyboardOpen &&
        pinnedScrollY != null &&
        Math.abs(window.scrollY - pinnedScrollY) > 1
      ) {
        window.scrollTo(0, pinnedScrollY);
      }
      // We measure every frame while the keyboard settles (see below), so commit
      // ONLY when a number actually moved — otherwise this re-renders the whole
      // conversation ~60 times a second for no reason.
      if (
        next.keyboardOpen === last.keyboardOpen &&
        next.offsetTop === last.offsetTop &&
        next.visibleHeight === last.visibleHeight
      ) {
        return;
      }
      last = next;
      setState(next);
    };
    // A SINGLE reading is not enough, and this is the second half of the bug.
    //
    // iOS fires visualViewport `resize` when the keyboard STARTS animating in,
    // and at that instant vv.height is often still the pre-keyboard height. It
    // then fires nothing more once the animation settles. So we would latch the
    // full-screen height and keep it — until some unrelated event (scrolling the
    // page to the top) happened to fire a listener and force a re-read. That is
    // exactly the "it only gets the right height after I scroll all the way up"
    // symptom.
    //
    // So we don't trust any one event: for a short window after anything that
    // could move the keyboard, we re-measure every frame until the numbers stop
    // changing. Cheap (a few hundred ms of rAF), and it makes the settled value
    // the one we end on rather than the one we happened to sample.
    let raf = 0;
    let deadline = 0;
    const pump = () => {
      update();
      if (performance.now() < deadline) {
        raf = requestAnimationFrame(pump);
      } else {
        raf = 0;
      }
    };
    /** Re-measure every frame for `ms`, so we catch the keyboard settling. */
    const remeasure = (ms = 600) => {
      deadline = performance.now() + ms;
      if (!raf) raf = requestAnimationFrame(pump);
    };

    update();
    const onViewportChange = () => remeasure();
    // Focus is the earliest signal the keyboard is coming — earlier than resize.
    const onFocusChange = () => remeasure();

    vv.addEventListener("resize", onViewportChange);
    vv.addEventListener("scroll", update);
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("focusin", onFocusChange);
    window.addEventListener("focusout", onFocusChange);
    // Rotation changes the visible rectangle wholesale.
    window.addEventListener("orientationchange", onViewportChange);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      vv.removeEventListener("resize", onViewportChange);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("scroll", update);
      window.removeEventListener("focusin", onFocusChange);
      window.removeEventListener("focusout", onFocusChange);
      window.removeEventListener("orientationchange", onViewportChange);
    };
  }, [active]);
  return state;
}

function newId(): string {
  const c = typeof crypto !== "undefined" ? crypto : undefined;
  return c?.randomUUID?.() ?? `m_${Math.random().toString(36).slice(2)}`;
}

/**
 * The instant, personalized, MOTIVATION-FIRST opener (see cancel-chat-greeting.ts;
 * keep in sync with the phase-1 prompt premise in src/lib/cancel-chat/prompt.ts).
 * The model asks the cancel question itself on its first reply; the reason chips
 * then render under THAT question.
 *
 * `replyOptions` are persisted as a data part so the admin transcript can show
 * the same motivation chips the customer saw (convertToModelMessages strips them).
 */
function greetingMessage(args: {
  subjectText: string;
  firstName: string | null;
  replyOptions: string[];
}): UIMessage {
  return {
    id: newId(),
    role: "assistant",
    parts: [
      { type: "text", text: buildCancelGreeting(args) },
      createReplyOptionsPart(args.replyOptions),
    ],
  } as UIMessage;
}

/**
 * The which-product question, frozen into the transcript as a real assistant
 * message the moment they pick. While they're deciding it's an app-rendered block
 * (see needsProductChoice); persisting it here is what keeps it on screen — as its
 * own bubble above the greeting — instead of vanishing when the block unmounts.
 */
function whichProductQuestionMessage(replyOptions: string[]): UIMessage {
  return {
    id: newId(),
    role: "assistant",
    parts: [
      { type: "text", text: WHICH_PRODUCT_QUESTION },
      createReplyOptionsPart(replyOptions),
    ],
  } as UIMessage;
}

/**
 * Stable id for the which-product answer bubble. It's a USER message, but it is
 * NOT an intake turn — the motivation opener still comes next. Anything that
 * detects "the first real user turn" (the motivation-answer capture, the
 * opening-turn chip source) must skip this id, or it mistakes the product pick for
 * the motivation answer and shows cancel-reason chips under the "what drew you?"
 * greeting.
 */
const PRODUCT_CHOICE_ANSWER_ID = "which-product-answer";

/** Their answer to it, as a user bubble — exactly as tapping any other chip reads. */
function productChoiceAnswerMessage(label: string): UIMessage {
  return {
    id: PRODUCT_CHOICE_ANSWER_ID,
    role: "user",
    parts: [{ type: "text", text: label }],
  } as UIMessage;
}

/** User turns that count as intake — excludes the which-product pick (see above). */
function isIntakeUserTurn(m: { role: string; id?: string }): boolean {
  return m.role === "user" && m.id !== PRODUCT_CHOICE_ANSWER_ID;
}

type ChatMessagePart = { type: string; toolCallId?: string };

/** Last turn is a completed record_reason / record_intake (intake → save handoff). */
function isIntakeCompleteAssistantTurn(
  messages: Array<{ role: string; parts?: readonly ChatMessagePart[] }>,
): boolean {
  const last = messages[messages.length - 1];
  if (!last || last.role !== "assistant") return false;
  const parts = last.parts ?? [];
  const hasIntake = parts.some(
    (p) => p.type === "tool-record_reason" || p.type === "tool-record_intake",
  );
  if (!hasIntake) return false;
  if (
    parts.some(
      (p) =>
        p.type.startsWith("tool-offer_") || p.type === "tool-confirm_cancel",
    )
  ) {
    return false;
  }
  return lastAssistantMessageIsCompleteWithToolCalls({
    messages: messages as UIMessage[],
  });
}

function intakeToolCallFromMessage(message: {
  parts?: readonly ChatMessagePart[];
}): { tool: string; toolCallId: string } | null {
  for (const p of message.parts ?? []) {
    if (p.type === "tool-record_reason" && p.toolCallId) {
      return { tool: "record_reason", toolCallId: p.toolCallId };
    }
    if (p.type === "tool-record_intake" && p.toolCallId) {
      return { tool: "record_intake", toolCallId: p.toolCallId };
    }
  }
  return null;
}

/** Local calendar date as YYYY-MM-DD. */
function toLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * The window the customer may pick their first billing date in when accepting the
 * size-up bundle: from tomorrow (the backend requires a date strictly after today)
 * out to six months, defaulting to their current next-billing date when that is
 * still in the future — the least-surprising choice.
 */
function bundleBillingDateWindow(currentNextBillingDate: string | null): {
  min: string;
  max: string;
  initial: string;
} {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const max = new Date(today);
  max.setMonth(max.getMonth() + 6);
  const min = toLocalYmd(tomorrow);
  const current = currentNextBillingDate?.slice(0, 10);
  const initial = current && current >= min ? current : min;
  return { min, max: toLocalYmd(max), initial };
}

/**
 * The billing-date step of accepting the size-up bundle, as a modal dialog. It
 * opens when the customer taps the accept CTA (e.g. "Switch to 64 servings");
 * only Confirm applies the bundle (via onConfirm → acceptOffer). The offer card
 * stays untouched behind it.
 */
function BundleBillingDateDialog({
  open,
  nextBillingDate,
  value,
  pending,
  onSelect,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  /** The subscription's current next-billing date, to seed the default. */
  nextBillingDate: string | null;
  /** The chosen date (YYYY-MM-DD), or null before a pick. */
  value: string | null;
  pending: boolean;
  onSelect: (ymd: string) => void;
  onConfirm: (chosen: string) => void;
  onCancel: () => void;
}) {
  const win = bundleBillingDateWindow(nextBillingDate);
  const chosen = value ?? win.initial;
  // test commit
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent
        showCloseButton={false}
        overlayClassName="tw:z-70"
        className="tw:z-71 tw:p-4 tw:max-w-sm tw:flex tw:flex-col tw:gap-3"
      >
        <DialogHeader className="tw:border-b tw:border-[#523D33]/15 tw:pb-3">
          <DialogTitle
            className="tw:text-left"
            style={{ color: "var(--so-text, #523D33)" }}
          >
            Pick your next delivery date
          </DialogTitle>
        </DialogHeader>
        <div className="tw:rounded-2xl tw:border tw:border-[#523D33]/15 tw:bg-white tw:p-3">
          <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-medium tw:text-[#523D33]">
            <CalendarDays className="tw:size-4 tw:shrink-0 tw:text-[#523D33]/70" />
            {formatSubscriptionDateForDisplay(chosen)}
          </div>
          <Calendar
            value={chosen}
            min={win.min}
            max={win.max}
            onSelect={onSelect}
          />
        </div>
        <DialogFooter className="tw:flex-col tw:gap-2">
          <Button
            type="button"
            className={PRIMARY_BRAND_BTN_CLASS}
            disabled={pending}
            onClick={() => onConfirm(chosen)}
          >
            Confirm
          </Button>
          <Button
            type="button"
            variant="outline"
            className={OUTLINE_BRAND_BTN_CLASS}
            onClick={onCancel}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Assistant-side chat bubble (left), reused for the offer questions. */
function AssistantBubble({
  children,
  variant = "gray",
}: {
  children: ComponentChildren;
  /**
   * "gray" is the default chat bubble. "card" is a white, bordered surface used
   * for the size-up bundle offer: its pink savings box is a 50%-opacity tint
   * meant to sit on WHITE (it matches the merge dialog that way) — on the gray
   * bubble the cool gray bleeds through and muddies the pink.
   */
  variant?: "gray" | "card";
}) {
  return (
    <div className="tw:flex tw:justify-start">
      <div
        className={cn(
          "tw:max-w-[90%] tw:rounded-2xl tw:rounded-bl-sm tw:px-3.5 tw:py-2.5 tw:text-sm tw:leading-snug",
          variant === "card"
            ? "tw:border tw:border-[#523D33]/15 tw:bg-white tw:shadow-sm"
            : "tw:bg-gray-100",
        )}
        style={{ color: "var(--so-text, #523D33)" }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * User-side chat bubble (right): the SENT message. Its solid brown fill and white
 * text are RESERVED — chat-bubble-styles.ts owns that fact, so a reply option can
 * never drift back into wearing it.
 */
function UserBubble({ children }: { children: ComponentChildren }) {
  return (
    <div className="tw:flex tw:justify-end">
      <div
        className={cn(
          "tw:max-w-[85%] tw:whitespace-pre-wrap tw:break-words tw:rounded-2xl tw:rounded-br-sm tw:px-3.5 tw:py-2.5 tw:text-sm tw:leading-snug",
          SENT_BUBBLE_FILL,
          SENT_BUBBLE_TEXT,
        )}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Reply options: things the customer COULD say, in the same right-hand column as the
 * things they HAVE said. A primary option used to be solid brown with white text —
 * pixel-identical to UserBubble above — so a live "Delay for 2 weeks" CTA read as a
 * choice already made. In this column, solid brown means SENT; see
 * chat-bubble-styles.ts.
 */
function ReplyOptions({
  options,
  disabled = false,
}: {
  options: ReplyOption[];
  disabled?: boolean;
}) {
  if (options.length === 0) return null;
  return (
    <div className="tw:flex tw:flex-col tw:items-end tw:gap-2">
      {options.slice(0, 5).map((o) => (
        <button
          key={o.label}
          type="button"
          disabled={disabled || o.pending}
          onClick={o.onClick}
          className={replyOptionClassName(o)}
        >
          {o.pending ? (
            <Loader2 className="tw:size-4 tw:animate-spin tw:mx-auto" />
          ) : (
            <span className="tw:flex tw:flex-col tw:items-end tw:gap-0.5">
              <span>{o.label}</span>
              {o.sublabel && (
                <span className="tw:text-xs tw:font-normal tw:text-[#523D33]/60">
                  {o.sublabel}
                </span>
              )}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

export function CancelSubscriptionChat({
  open,
  onOpenChange,
  product,
  isBeforeSecondShipment,
  isFlexibleSubscription,
  changeFrequencyAvailable,
  trackingShop: trackingShopProp,
  customerEmail,
  customerName,
  siblingProductTitles,
  contractProducts,
  onSubmit,
  onDelayDate,
  onChangeFrequencyTo,
  onFatalError,
  cancelAfterDeliveryEnabled = false,
}: CancelSubscriptionChatProps) {
  const cancelChatTestMode = isCancelChatTestMode();
  const trackingShop = cancelChatTestMode ? null : trackingShopProp;
  const isDesktop = useIsDesktop();
  const { keyboardOpen, offsetTop, visibleHeight } = useKeyboardViewport(
    open && !isDesktop,
  );
  const queryClient = useQueryClient();
  const subscriptionId = product.shopify_subscription_id;

  // --- what are we cancelling? (the which-product step) -------------------
  //
  // Every line on this contract. `product` is merely the row they CLICKED, which
  // on a 2+ line contract says nothing about what they want to stop.
  const contractLines = useMemo(
    () => (contractProducts?.length ? contractProducts : [product]),
    [contractProducts, product],
  );
  const isMultiLine = contractLines.length > 1;

  /**
   * Their answer to "which product would you like to cancel?" — null until they
   * pick, and permanently null for a single-line contract (there is nothing to
   * ask) and for multi-line contracts before the 2nd shipment (partial cancel is
   * not offered — the early-tenure flow ends in switch-to-one-time instead).
   * "everything" is today's whole-contract flow, unchanged.
   */
  const [productChoice, setProductChoice] = useState<
    | { kind: "single"; product: ManageSubscriptionSheetProduct }
    | { kind: "everything" }
    | null
  >(null);
  /** Multi-line, on/after 2nd shipment, and they haven't picked — opens on the question. */
  const needsProductChoice =
    isMultiLine && !isBeforeSecondShipment && productChoice === null;
  /**
   * Scoped to ONE line. This is the flag the whole partial flow hangs off: it
   * pares the save ladder down to swap (every other lever is whole-contract),
   * rewrites the confirm/success copy, tells the model to reassure rather than
   * announce a cancellation, and blocks the full-cancel error fallback.
   */
  const isSingleLineScope = productChoice?.kind === "single";
  /**
   * The line this conversation is ABOUT. Once they've picked, that's their pick;
   * otherwise the row they clicked. Everything product-scoped below reads this
   * rather than `product` — the swap they're offered, the greeting, the chips,
   * the tracking — because after the pick, `product` is just the row that
   * happened to be on screen when they opened the sheet.
   */
  const activeProduct = isSingleLineScope ? productChoice.product : product;
  /** The lines that MUST keep shipping — named in the confirm + success copy. */
  const retainedTitles = useMemo(
    () =>
      isSingleLineScope
        ? [
          ...new Set(
            contractLines
              .filter((l) => l.id !== activeProduct.id)
              .map((l) => l.title?.trim())
              .filter((t): t is string => !!t),
          ),
        ]
        : [],
    [isSingleLineScope, contractLines, activeProduct.id],
  );

  // Swap-offer material: the AI-composable candidate menu, the deterministic
  // default bundle, and the value rules echoed by the backend. All value
  // enforcement happens in shared/swap-offer — the model only ever picks
  // among pre-qualified products, and its pick is re-validated.
  //
  // Scoped to `activeProduct`, not `product`: in single-line scope the swap must
  // replace the LINE THEY PICKED, priced against it and excluding it from the
  // candidate menu. Until they pick (and on the everything / single-line-contract
  // paths) activeProduct IS product, so this is unchanged there; the query key
  // carries activeProduct.id, so it refetches for the chosen line on the pick.
  //
  // Effective line count: partial cancel is always "1 line" (replace that line
  // only). Whole-contract cancel uses the contract length (2 → exactly 2 vs
  // sum; 3+ → no swap).
  //
  // Swap price band is on delivery TOTAL (unit × qty), not unit alone — so a
  // 3× $0.50 line anchors at $1.50. New swap lines are always qty 1, and
  // combo_total is the sum of those unit prices.
  const effectiveLineCount = isSingleLineScope ? 1 : contractLines.length;
  const currentPrice =
    effectiveLineCount === 2
      ? contractPerDeliveryTotal(contractLines)
      : lineTotals(activeProduct).net;
  const swapQueryEnabled =
    open &&
    !isBeforeSecondShipment &&
    subscriptionId != null &&
    effectiveLineCount < 3;
  const swapQuery = useQuery({
    ...swapOfferQueryOptions({
      subscriptionId,
      currentProductId: activeProduct.id,
      currentPrice,
      lineCount: effectiveLineCount,
    }),
    enabled: swapQueryEnabled,
  });
  // Upstream swap-offer is slow (~20–30s). Until the first result lands, treat
  // "no swap bundle" as unknown — not "no swap exists". Otherwise dislike
  // empties the table and unlocks confirm before the offer can appear.
  const swapOfferAwaiting = swapQueryEnabled && swapQuery.isPending;
  const swapOfferAwaitingRef = useRef(swapOfferAwaiting);
  swapOfferAwaitingRef.current = swapOfferAwaiting;
  const swapQueryEnabledRef = useRef(swapQueryEnabled);
  swapQueryEnabledRef.current = swapQueryEnabled;
  /** Intake → save auto-continue held until swap-offer resolves (see sendAutomaticallyWhen). */
  const pendingSaveContinueRef = useRef(false);
  const saveResumeStartedRef = useRef(false);
  const toSwapCandidate = (p: {
    id: number;
    title: string;
    image_url: string | null;
    price: number | null;
    variantId?: number | null;
    variantTitle?: string | null;
  }): SwapCandidate => ({
    id: p.id,
    title: p.title,
    imageUrl: p.image_url,
    price: p.price,
    variantId: p.variantId ?? null,
    variantTitle: p.variantTitle ?? null,
  });
  /** Deterministic default bundle — the card fallback and the lever gate. */
  const swapDefaultBundle: SwapCandidate[] = useMemo(
    () => (swapQuery.data?.products ?? []).map(toSwapCandidate),
    [swapQuery.data],
  );
  // Live ref so the (stable) tool handler can validate AI compositions.
  const swapOfferRef = useRef(swapQuery.data);
  swapOfferRef.current = swapQuery.data;

  // Size-up bundle material. The bundle is computed ENTIRELY client-side from the
  // product detail's variants + Subscribe & Save selling plans — there is no
  // pricing endpoint (unlike the swap). Fetch it only in the save phase; the
  // compute itself gates to Rose Nectar / single-line / a qualifying saving.
  // Target shipping is previewed via draftOrderCalculate so free-shipping
  // thresholds are reflected (never reuse the current deliveryPrice).
  const productDetailQuery = usePurchasedProductDetail(
    product.id,
    open && !isBeforeSecondShipment,
  );
  const discountPercent = subscriptionPromoPercent(
    product.subscription_discounts,
  );
  // Build the pure-compute inputs from the product detail + contract facts. Shared
  // by the eligibility memo (menu gate) and the display/accept memo (which passes
  // the recorded root so the pick is right — see bundleOffer below).
  const buildBundleArgs = (
    subReason: CancelSubReasonId | null,
    targetShippingCost: number | null,
  ) => {
    const detail = productDetailQuery.data;
    const lines = contractProducts?.length ? contractProducts : [product];
    return {
      productTitle: product.title,
      isSingleLine: lines.length === 1,
      cadence:
        product.subscription_billing_interval_count != null &&
          product.subscription_billing_interval != null
          ? {
            interval: product.subscription_billing_interval_count,
            intervalUnit: product.subscription_billing_interval,
          }
          : null,
      currentVariantId: detail?.variant_id ?? null,
      currentVariantTitle:
        detail?.variants?.find((v) => v.shopify_id === detail?.variant_id)
          ?.title ??
        product.variant_title ??
        null,
      variants: detail?.variants ?? [],
      contractPerDeliveryTotal: contractPerDeliveryTotal(lines),
      currentShippingCost: product.subscription_shipping_price ?? null,
      targetShippingCost,
      discountPercent,
      subReason,
    };
  };
  // Structural pick for the shipping-preview request. Prefer a product-only
  // qualifier; if none (saving only appears once free shipping lands), fall
  // back to an optimistic $0 target-shipping compute so we still know which
  // variant to quote.
  const bundlePreviewPick = useMemo(() => {
    const productOnly = computeSizeUpBundle(buildBundleArgs(null, null));
    if (productOnly) return productOnly;
    const currentShip = product.subscription_shipping_price;
    if (currentShip != null && currentShip > 0) {
      return computeSizeUpBundle(buildBundleArgs(null, 0));
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pure over these sources
  }, [product, contractProducts, productDetailQuery.data, discountPercent]);

  const bundleShippingQuery = useQuery({
    queryKey: [
      "subscription-shipping-preview",
      subscriptionId,
      bundlePreviewPick?.targetVariantId ?? null,
      bundlePreviewPick?.quantity ?? 1,
    ],
    queryFn: () =>
      previewSubscriptionShipping(subscriptionId!, {
        variantId: bundlePreviewPick!.targetVariantId,
        quantity: bundlePreviewPick!.quantity,
      }),
    enabled:
      open &&
      !isBeforeSecondShipment &&
      subscriptionId != null &&
      bundlePreviewPick != null,
    staleTime: 5 * 60_000,
  });

  const resolvedTargetShipping: number | null =
    bundleShippingQuery.isFetched
      ? (bundleShippingQuery.data?.shippingPrice ?? null)
      : null;

  // Eligibility is sub-reason-INDEPENDENT (the same candidates qualify; only the
  // PICK differs), so the MENU can gate on a null-root compute here. The card and
  // the accept payload below recompute with the recorded root for the right pick.
  // While the shipping preview is in flight, optimistically assume free shipping
  // when they currently pay a fee (usual for size-ups over $100) so the lever
  // can appear; once fetched, the real rate replaces it.
  const bundleEligible = useMemo(() => {
    const ship =
      resolvedTargetShipping ??
      (bundleShippingQuery.isFetching &&
        (product.subscription_shipping_price ?? 0) > 0
        ? 0
        : null);
    return computeSizeUpBundle(buildBundleArgs(null, ship));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pure over these sources
  }, [
    product,
    contractProducts,
    productDetailQuery.data,
    discountPercent,
    resolvedTargetShipping,
    bundleShippingQuery.isFetching,
  ]);

  const cancelContext = useMemo(
    () =>
      buildCancelChatContext({
        product,
        isBeforeSecondShipment,
        isFlexible: isFlexibleSubscription,
        changeFrequencyAvailable,
        customerFirstName: customerName?.trim().split(/\s+/)[0] || null,
        siblingProductTitles,
        swapCandidates: (swapQuery.data?.candidates ?? [])
          .filter((c) => typeof c.price === "number" && c.price > 0)
          .map((c) => ({
            // Selection key is the variant id when present — the model picks a
            // specific SKU; the widget re-resolves product id for fulfillment.
            id: c.variantId ?? c.id,
            title: c.variantTitle
              ? `${c.title} — ${c.variantTitle}`
              : c.title,
            price: c.price as number,
          })),
        contractLines: contractProducts,
        bundleAvailable: bundleEligible != null,
        // Single-line scope: tell the server this is a partial removal of the
        // chosen line, so the prompt reassures the retained lines keep shipping
        // and never announces a whole-subscription cancellation (Slice 3).
        singleLineScope: isSingleLineScope
          ? { removedTitle: activeProduct.title, retainedTitles }
          : null,
        cancelAfterDeliveryEnabled,
      }),
    [
      product,
      isBeforeSecondShipment,
      isFlexibleSubscription,
      changeFrequencyAvailable,
      customerName,
      siblingProductTitles,
      swapQuery.data,
      contractProducts,
      bundleEligible,
      isSingleLineScope,
      activeProduct.title,
      retainedTitles,
      cancelAfterDeliveryEnabled,
    ],
  );
  const ctxRef = useRef(cancelContext);
  ctxRef.current = cancelContext;

  // What this conversation is about — the product, both products, or the whole
  // subscription. Contract-scoped cards speak about THIS, not `product.title`.
  //
  // Once they've scoped to ONE line, the subject IS that line with no siblings
  // (`buildCancelSubject(chosen, [])`): the greeting becomes "what drew you to
  // A?", the swap card names A, and nothing speaks for the whole contract — the
  // sibling lines aren't being cancelled. Until then it's the whole-contract
  // subject exactly as before.
  const subject = useMemo(
    () =>
      isSingleLineScope
        ? buildCancelSubject(activeProduct.title, [])
        : buildCancelSubject(product.title, siblingProductTitles ?? []),
    [isSingleLineScope, activeProduct.title, product.title, siblingProductTitles],
  );

  // The current cadence, normalized. StayAI reports the unit uppercase
  // ("MONTH"); the API and StayAiIntervalUnit are lowercase, so normalize
  // (matches ChangeFrequencyDialog) — an unexpected unit yields no presets
  // rather than a bad request.
  const freqBase = useMemo<{
    count: number;
    unit: StayAiIntervalUnit;
  } | null>(() => {
    const count = product.subscription_billing_interval_count;
    const unitRaw = product.subscription_billing_interval;
    if (count == null || !Number.isFinite(count) || count < 1 || !unitRaw) {
      return null;
    }
    const lower = unitRaw.toLowerCase();
    const allowed: readonly StayAiIntervalUnit[] = [
      "day",
      "week",
      "month",
      "year",
    ];
    if (!allowed.includes(lower as StayAiIntervalUnit)) return null;
    return { count, unit: lower as StayAiIntervalUnit };
  }, [
    product.subscription_billing_interval_count,
    product.subscription_billing_interval,
  ]);

  const whyBoughtRef = useRef("");
  const whyDetailRef = useRef("");
  // The customer's OWN answer to the opening question, captured verbatim as they
  // send it — as opposed to whyBoughtRef above, which is the model's paraphrase and
  // only exists once record_intake fires. Ground truth vs the model's reading of it;
  // see cancel-chat-intake.ts for why we keep both.
  const rawMotivationRef = useRef<MotivationAnswer | null>(null);
  // Verbatim cancel-driver answers (chip taps + typed), accumulated after the
  // opening motivation and until record_reason. Prefer this over the model's
  // whyDetail paraphrase when persisting / emailing support.
  const rawCancelWordsRef = useRef<string[]>([]);
  /** The chips on screen for the opening question, so a tap-rate has a denominator. */
  const openingChipsRef = useRef<string[]>([]);
  const motivationShownRef = useRef(false);
  /** Composer textarea — focused when they tap the "Other" cancel-reason chip. */
  const composerInputRef = useRef<HTMLTextAreaElement>(null);
  const reasonRef = useRef<CancelSubscriptionReasonId | null>(null);
  // The ROOT the model recorded at intake (record_intake's `subReason`). Rides
  // back to the server on every save-phase turn via `activeOffer` so the save
  // prompt can name the lever that actually answers it (see
  // src/lib/cancel-chat/prompt.ts's `subReasonNote`) — without this the whole
  // "one offer, matched to what they said" design has no signal to route on.
  const subReasonRef = useRef<CancelSubReasonId | null>(null);
  const otherTextRef = useRef("");
  const offerDeclinedRef = useRef(false);
  // Levers exhausted by an idea decline (chip / decline_*) — skip 2nd delay/freq slot.
  // Ref so auto-continue after addToolResult reads the updated menu immediately.
  const ideaExhaustedOffersRef = useRef<Set<EligibleOffer>>(new Set());
  const [ideaExhaustedVersion, setIdeaExhaustedVersion] = useState(0);
  const [reasonSet, setReasonSet] = useState(false);
  const [saveDeferredForSwap, setSaveDeferredForSwap] = useState(false);
  const [currentReason, setCurrentReason] =
    useState<CancelSubscriptionReasonId | null>(null);

  // Presented offer cards (in order). Positioned in the transcript by their
  // tool-call id; each resolves to accepted/declined via its card buttons.
  const [cards, setCards] = useState<OfferCard[]>([]);
  const cardsRef = useRef(cards);
  cardsRef.current = cards;
  // After switch is shown (switch-only mode), an explicit "can I delay / space
  // out?" re-opens that lever alongside switch until they decline it or leave.
  const [explicitAskReopen, setExplicitAskReopen] =
    useState<ExplicitRetentionAsk | null>(null);
  const [success, setSuccess] = useState<SuccessKind | null>(null);
  const successRef = useRef<SuccessKind | null>(null);
  /** Set by rawSwap when the orchestrated swap-request completes immediately. */
  const swapCompletedRef = useRef(false);
  // A fatal chat error AFTER they've scoped to one line: we can't hand this to the
  // parent's full-cancel fallback (that would cancel the whole contract for a
  // partial-intent customer), so we hold the customer in an in-chat recovery
  // state instead. Only ever set in single-line scope; see the fatal-error effect.
  const [singleLineFatalError, setSingleLineFatalError] = useState(false);
  const [pending, setPending] = useState(false);
  // Label of the option currently being submitted, so only the clicked button
  // shows a loader (the rest of the card's options are just disabled).
  const [submittingLabel, setSubmittingLabel] = useState<string | null>(null);
  const [input, setInput] = useState("");
  // Accepting the size-up bundle is a TWO-step tap: the accept CTA first reveals
  // a billing-date calendar in the same card (this holds that card's id), and only
  // confirming a date applies it. `bundleBillingDate` is the date they've picked.
  const [bundleDatePickerFor, setBundleDatePickerFor] = useState<string | null>(
    null,
  );
  const [bundleBillingDate, setBundleBillingDate] = useState<string | null>(
    null,
  );

  // Live executor refs so the (stable) tool handler + transport body read the
  // latest props/state without re-subscribing.
  const onDelayDateRef = useRef(onDelayDate);
  onDelayDateRef.current = onDelayDate;
  const onChangeFrequencyToRef = useRef(onChangeFrequencyTo);
  onChangeFrequencyToRef.current = onChangeFrequencyTo;

  // Per-turn gating state the widget owns and sends to the model each turn.
  const activeOfferRef = useRef<{
    currentReasonId: string | null;
    currentSubReasonId: CancelSubReasonId | null;
    availableOffers: EligibleOffer[];
    canConfirmCancel: boolean;
    retentionReopenedAfterSwitch: boolean;
  }>({
    currentReasonId: null,
    currentSubReasonId: null,
    availableOffers: [],
    canConfirmCancel: false,
    retentionReopenedAfterSwitch: false,
  });

  // Identifies this conversation for transcript persistence + tracking.
  // Regenerated only on conversation reset (see the `if (reset)` block below) so a
  // resumed conversation keeps its id.
  const conversationIdRef = useRef<string>("");
  const orderCount = product.subscription_order_count;
  const cancelTrackProps = () =>
    cancelFlowTrackingProps({
      cancelAttemptId: conversationIdRef.current,
      orderCount,
    });

  const transport = useMemo(() => {
    // Identify the customer to the backend, matching sibling widget routes: the
    // cancel-chat route requires an identifier to gate its paid Gemini calls.
    const identifier = getCustomerIdentifier();
    const query = new URLSearchParams();
    if (identifier?.type === "id") {
      query.set("shopify_customer_id", String(identifier.shopify_customer_id));
    } else if (identifier?.type === "token") {
      query.set("buy_again_token", identifier.buy_again_token);
    } else if (customerEmail) {
      query.set("customer_email", customerEmail);
    }
    if (Number.isFinite(product.id)) {
      query.set("product_id", String(product.id));
    }
    const qs = query.toString();
    return new DefaultChatTransport<UIMessage>({
      api: `${API_BASE.replace(/\/$/, "")}/api/widget/buy-again/subscriptions/${subscriptionId}/cancel-chat${qs ? `?${qs}` : ""}`,
      credentials: "omit",
      body: () => ({
        context: ctxRef.current,
        activeOffer: activeOfferRef.current,
        conversationId: conversationIdRef.current,
        isTest: cancelChatTestMode,
      }),
    });
  }, [subscriptionId, customerEmail, cancelChatTestMode, product.id]);

  const addToolResultRef = useRef<
    | ((args: { tool: string; toolCallId: string; output: unknown }) => void)
    | null
  >(null);
  const setMessagesRef = useRef<
    ((updater: (prev: UIMessage[]) => UIMessage[]) => void) | null
  >(null);
  const voiceRepliedToolCallIdsRef = useRef(new Set<string>());

  /** Voice accept_* tools run the same mutations as card taps (see handleAcceptToolRef). */
  const handleAcceptToolRef = useRef<
    (toolCall: {
      toolName: string;
      toolCallId: string;
      input?: unknown;
    }) => Promise<void>
  >(async () => { });
  /** Voice decline_* tools reuse declineOffer (see handleDeclineToolRef). */
  const handleDeclineToolRef = useRef<
    (toolCall: {
      toolName: string;
      toolCallId: string;
      input?: unknown;
    }) => Promise<void>
  >(async () => { });

  const handleToolCall = useCallback(
    async ({
      toolCall,
    }: {
      toolCall: { toolName: string; toolCallId: string; input?: unknown };
    }) => {
      const name = toolCall.toolName;
      const input = (toolCall.input ?? {}) as Record<string, unknown>;

      // Suggested-reply chips for the question the model just asked. The chips are
      // read straight from this tool part in the transcript; we only need to report
      // a result so the turn is valid. We do NOT auto-continue afterwards (see
      // sendAutomaticallyWhen) — the model waits for the customer's tap or reply.
      if (name === "suggest_replies") {
        addToolResultRef.current?.({
          tool: name,
          toolCallId: toolCall.toolCallId,
          output: { ok: true },
        });
        return;
      }

      // Intake (cancellation reason + driver) captured in a single tool call.
      if (name === "record_reason" || name === "record_intake") {
        whyBoughtRef.current =
          typeof input.whyBought === "string" ? input.whyBought : "";
        whyDetailRef.current =
          typeof input.whyDetail === "string" ? input.whyDetail : "";
        // The model may invent an id for a reason not in the fixed enum (e.g.
        // "moving_abroad_soon") rather than force-fitting it to "other" — that
        // invented id rides here in preference to reasonId when present.
        const invent = input.invent as { id?: string } | undefined;
        const resolvedId =
          typeof invent?.id === "string" &&
          invent.id.length > 0 &&
          invent.id !== "unused"
            ? invent.id
            : typeof input.reasonId === "string"
              ? input.reasonId
              : "";
        const id = resolvedId as CancelSubscriptionReasonId;
        reasonRef.current = id;
        otherTextRef.current =
          typeof input.otherText === "string" ? input.otherText : "";
        // Untrusted model output — validate against the shared enum so a
        // hallucinated id can't ride into the server prompt's `Root: ...`
        // lookup as anything other than null.
        subReasonRef.current = isCancelSubReasonId(input.subReason)
          ? input.subReason
          : null;
        setCurrentReason(id);
        setReasonSet(true);
        if (trackingShop) {
          // whyBought/whyDetail ride on this event so the intake survives even
          // when the customer closes the chat without cancelling — it is the
          // richest churn signal the chatbot collects.
          //
          // why_bought here is the MODEL's paraphrase. The customer's verbatim
          // answer rides alongside it (why_bought_raw + why_bought_source), so the
          // two can be compared and the paraphrase audited rather than trusted.
          trackBuyAgainWidgetInteract(
            "subscription_cancel_reason_selected",
            trackingShop,
            {
              productId: product.id,
              subscription_contract_id: subscriptionId ?? undefined,
              reason: id,
              // The ROOT behind the reason — the thing that actually picks the save
              // (see shared/cancel-sub-reason). The model has recorded it since the
              // chatbot shipped and it rides to the server prompt on every save
              // turn, but it was never tracked: "too expensive" reached PostHog
              // with no way to tell a tight budget from a value judgement, which
              // are opposite problems with opposite answers.
              sub_reason: subReasonRef.current ?? undefined,
              ...(rawMotivationRef.current
                ? motivationEventProps(rawMotivationRef.current)
                : {}),
              why_bought: whyBoughtRef.current.trim() || undefined,
              // Model paraphrase — verbatim cancel answers ride as why_detail_raw.
              why_detail: whyDetailRef.current.trim() || undefined,
              why_detail_raw:
                rawCancelWordsRef.current
                  .map((w) => w.trim())
                  .filter(Boolean)
                  .join(" — ") || undefined,
              other_text: otherTextRef.current.trim() || undefined,
              ...cancelTrackProps(),
            },
          );
          // Also its own event, so the root is directly queryable without having to
          // filter the broader intake event down to the rows that happen to carry it.
          if (subReasonRef.current) {
            trackBuyAgainWidgetInteract(
              "subscription_cancel_sub_reason_selected",
              trackingShop,
              {
                productId: product.id,
                subscription_contract_id: subscriptionId ?? undefined,
                reason: id,
                sub_reason: subReasonRef.current,
                why_detail: whyDetailRef.current.trim() || undefined,
                ...cancelTrackProps(),
              },
            );
          }
        }
        if (swapQueryEnabledRef.current && swapOfferAwaitingRef.current) {
          pendingSaveContinueRef.current = true;
          setSaveDeferredForSwap(true);
        }
        addToolResultRef.current?.({
          tool: name,
          toolCallId: toolCall.toolCallId,
          output: { ok: true },
        });
        return;
      }

      // Offer / confirm tools: render the card and DEFER the tool result — it is
      // reported only when the customer accepts or declines on the card, so the
      // model pauses until then and escalates on the reported outcome.
      if (name.startsWith("offer_") || name === "confirm_cancel") {
        const kind: CardKind =
          name === "confirm_cancel"
            ? "confirm"
            : (name.slice("offer_".length) as EligibleOffer);
        const message =
          typeof input.message === "string" ? input.message : undefined;
        // Model-proposed in-lever terms: untrusted, so clamp to the widget's
        // bounds; anything invalid degrades to the standard presets.
        const proposedWeeks =
          kind === "delay" ? clampProposedDelayWeeksList(input.weeks) : null;
        const proposedMultipliers =
          kind === "change_frequency"
            ? clampProposedFrequencyMultipliersList(input.multipliers)
            : null;
        // Swap: always use the API's deterministic default bundle for now.
        // AI productIds composition is kept below (commented) for later.
        let bundle: OfferCard["bundle"];
        if (kind === "swap") {
          const offer = swapOfferRef.current;
          bundle = {
            products: (offer?.products ?? []).map((p) => ({
              id: p.id,
              title: p.title,
              imageUrl: p.image_url,
              price: p.price,
              variantId: p.variantId,
              variantTitle: p.variantTitle,
            })),
            comboTotal: offer?.comboTotal ?? null,
          };
          // Persist what the card shows so admin review can render thumbs.
          const swapProducts = serializeOfferProducts(bundle.products);
          if (swapProducts.length > 0) {
            setMessagesRef.current?.((prev) =>
              prev.map((m) => {
                const parts = [
                  ...((m.parts ?? []) as Array<{
                    type?: string;
                    toolCallId?: string;
                    data?: { toolCallId?: string };
                  }>),
                ];
                const hasTool = parts.some(
                  (p) => p.toolCallId === toolCall.toolCallId,
                );
                if (!hasTool) return m;
                if (
                  parts.some(
                    (p) =>
                      p.type === "data-offer-details" &&
                      p.data?.toolCallId === toolCall.toolCallId,
                  )
                ) {
                  return m;
                }
                return {
                  ...m,
                  parts: [
                    ...parts,
                    createOfferDetailsPart({
                      toolCallId: toolCall.toolCallId,
                      options: ["Swap my subscription", "Not now"],
                      products: swapProducts,
                    }),
                  ],
                } as UIMessage;
              }),
            );
          }
          // --- AI-composed swap (temporarily disabled) -------------------
          // const pickedIds = Array.isArray(input.productIds)
          //   ? (input.productIds as unknown[]).filter(
          //       (v): v is number =>
          //         typeof v === "number" && Number.isInteger(v),
          //     )
          //   : null;
          // const validated =
          //   pickedIds && offer
          //     ? validateSwapSelection(
          //         pickedIds,
          //         offer.candidates.map((c) => ({
          //           id: c.id,
          //           title: c.title,
          //           image_url: c.image_url,
          //           price: c.price,
          //           rating: 0,
          //           variant_id: c.variantId,
          //           variant_title: c.variantTitle,
          //         })),
          //         offer.currentPrice ?? currentPrice,
          //         offer.config ?? DEFAULT_SWAP_OFFER_CONFIG,
          //       )
          //     : null;
          // if (validated) {
          //   bundle = {
          //     products: validated.products.map((p) => ({
          //       id: p.id,
          //       title: p.title,
          //       imageUrl: p.image_url,
          //       price: p.price,
          //       variantId: p.variant_id ?? null,
          //       variantTitle: p.variant_title ?? null,
          //     })),
          //     comboTotal: validated.comboTotal,
          //   };
          // }
          // ----------------------------------------------------------------
        }
        setCards((prev) =>
          prev.some((c) => c.id === toolCall.toolCallId)
            ? prev
            : [
              ...prev,
              {
                id: toolCall.toolCallId,
                kind,
                status: "pending",
                message,
                proposedWeeks: proposedWeeks ?? undefined,
                proposedMultipliers: proposedMultipliers ?? undefined,
                bundle,
                rePitch: prev.some((c) => c.kind === kind) || undefined,
              },
            ],
        );
        return;
      }

      if (name.startsWith("accept_")) {
        await handleAcceptToolRef.current(toolCall);
        return;
      }

      if (name.startsWith("decline_")) {
        await handleDeclineToolRef.current(toolCall);
        return;
      }

      addToolResultRef.current?.({
        tool: name,
        toolCallId: toolCall.toolCallId,
        output: { ok: false, error: `Unknown tool: ${name}` },
      });
    },
    // currentPrice: the swap-composition fallback closes over it, and it's scoped
    // to activeProduct — so this must re-bind when the chosen line's price changes.
    [product.id, currentPrice, subscriptionId, trackingShop],
  );

  const { messages, sendMessage, addToolResult, status, setMessages, error } =
    useChat({
      transport,
      // Auto-continue after a resolved tool result (intake → lead offer; decline →
      // escalate). Never continue once we've reached a terminal success card, and
      // never right after the model offered suggested replies — that turn is a
      // question awaiting the customer's tap/typed answer, not a step to auto-advance.
      sendAutomaticallyWhen: ({ messages }) => {
        if (successRef.current) return false;
        const last = messages[messages.length - 1];
        const offeredSuggestions =
          last?.role === "assistant" &&
          (last.parts ?? []).some(
            (p) => (p as { type: string }).type === "tool-suggest_replies",
          );
        if (offeredSuggestions) return false;
        if (!lastAssistantMessageIsCompleteWithToolCalls({ messages })) {
          return false;
        }
        // Hold intake → save until swap-offer resolves. The upstream call is
        // slow (~20–30s); entering save with an empty swap menu dead-ends the
        // customer or races confirm before swap exists.
        if (
          pendingSaveContinueRef.current &&
          swapOfferAwaitingRef.current &&
          isIntakeCompleteAssistantTurn(messages)
        ) {
          return false;
        }
        pendingSaveContinueRef.current = false;
        return true;
      },
      onToolCall: handleToolCall,
    });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addToolResultRef.current = addToolResult as any;
  setMessagesRef.current = setMessages;

  const isTyping = status === "submitted" || status === "streaming";
  const awaitingSwapForSave = saveDeferredForSwap && swapOfferAwaiting;
  const chatBusy = isTyping || awaitingSwapForSave;

  // Resume intake → save once swap-offer lands (sendAutomaticallyWhen only runs
  // after addToolResult — re-report intake output so auto-continue can proceed).
  useEffect(() => {
    if (swapOfferAwaiting || !pendingSaveContinueRef.current) return;
    if (chatBusy || successRef.current) return;
    if (!swapQueryEnabledRef.current) {
      pendingSaveContinueRef.current = false;
      setSaveDeferredForSwap(false);
      return;
    }
    const last = messages[messages.length - 1];
    if (!last || !isIntakeCompleteAssistantTurn(messages)) {
      pendingSaveContinueRef.current = false;
      setSaveDeferredForSwap(false);
      return;
    }
    const intakeTool = intakeToolCallFromMessage(last);
    if (!intakeTool || saveResumeStartedRef.current) return;
    saveResumeStartedRef.current = true;
    pendingSaveContinueRef.current = false;
    setSaveDeferredForSwap(false);
    void addToolResultRef.current?.({
      tool: intakeTool.tool,
      toolCallId: intakeTool.toolCallId,
      output: { ok: true },
    });
  }, [swapOfferAwaiting, chatBusy, messages]);

  // A transport/stream failure is fatal for the chat: signal the parent once so it
  // can fall back to the deterministic dialog (this is a cancel surface — the
  // customer must never get trapped by a flaky AI turn).
  //
  // EXCEPT once they've scoped to one line. The parent's fallback is the
  // whole-contract cancel dialog, and routing a partial-intent customer there
  // could silently cancel EVERYTHING off the back of a flaky AI turn — the one
  // outcome this whole flow exists to prevent. So in single-line scope we keep
  // them in-chat with a recovery card and never fire onFatalError. The scope is
  // read through a ref because this effect only re-runs on `error`, and the pick
  // happens on an earlier render.
  const onFatalErrorRef = useRef(onFatalError);
  onFatalErrorRef.current = onFatalError;
  const isSingleLineScopeRef = useRef(isSingleLineScope);
  isSingleLineScopeRef.current = isSingleLineScope;
  const firedErrorRef = useRef(false);
  useEffect(() => {
    if (error && !firedErrorRef.current) {
      firedErrorRef.current = true;
      if (isSingleLineScopeRef.current) {
        setSingleLineFatalError(true);
      } else {
        onFatalErrorRef.current?.();
      }
    }
  }, [error]);

  // --- gating (widget owns eligibility) ----------------------------------
  const shownOfferCounts = useMemo(() => {
    const counts = new Map<CardKind, number>();
    for (const c of cards) {
      if (c.kind === "confirm") continue;
      counts.set(c.kind, (counts.get(c.kind) ?? 0) + 1);
    }
    return counts;
  }, [cards]);
  // Delay and change-frequency may be presented TWICE — the second time only as
  // a terms-adjusted re-pitch (the prompt binds the model to that); every other
  // lever is one-shot.
  const maxPresentations = (o: EligibleOffer): number =>
    o === "delay" || o === "change_frequency" ? 3 : 1;

  // The bundle to actually SHOW / apply, picked for the recorded root (budget_tight
  // → smallest upfront; price_vs_value → cheapest cost/month). Keyed on
  // currentReason, which flips in the same render that sets subReasonRef at intake.
  // Uses the previewed target shipping when available (shippingIncluded); falls
  // back to product-only while the preview is still in flight.
  const bundleOffer = useMemo<SizeUpBundle | null>(() => {
    const ship =
      resolvedTargetShipping ??
      (bundleShippingQuery.isFetching &&
        (product.subscription_shipping_price ?? 0) > 0
        ? 0
        : null);
    return computeSizeUpBundle(buildBundleArgs(subReasonRef.current, ship));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- root rides on currentReason
  }, [
    product,
    contractProducts,
    productDetailQuery.data,
    currentReason,
    discountPercent,
    resolvedTargetShipping,
    bundleShippingQuery.isFetching,
  ]);
  // The bundle is a PRICE-root lever only: it enters the menu for every reason (like
  // swap/discount), but is dropped per-turn unless the recorded root is a price one
  // AND a bundle actually qualifies — so a "moving house" cancel never sees it.
  const bundleShowable =
    (subReasonRef.current === "budget_tight" ||
      subReasonRef.current === "price_vs_value") &&
    bundleOffer != null;

  const anyOfferDeclined = cards.some(
    (c) => c.kind !== "confirm" && c.status === "declined",
  );

  const remainingOffers = useMemo<EligibleOffer[]>(() => {
    if (!currentReason) return [];
    // Single-line scope: swap is the ONLY save. Every whole-contract lever —
    // delay, change-frequency, pause, switch-to-one-time — re-times or
    // re-prices the OTHER lines too (Stay AI's delay/frequency act on the
    // whole contract, not one line), so none can honestly answer "drop just this
    // one". Only a swap replaces the chosen line with something they'd keep,
    // leaving its siblings untouched. Gate it on a real swap bundle and drop it
    // once shown, so a decline (or no bundle at all) falls through to
    // confirm-remove — never a dead end, and never a re-offer of the same swap.
    if (isSingleLineScope) {
      const swapShown =
        (shownOfferCounts.get("swap") ?? 0) >= maxPresentations("swap");
      return swapDefaultBundle.length >= 1 && !swapShown ? ["swap"] : [];
    }

    const reasonEligible = getEligibleOffersForReason(
      cancelContext.eligibleOffersByReason,
      currentReason,
    );
    const delayEligible = reasonEligible.includes("delay");
    const freqEligible =
      reasonEligible.includes("change_frequency") &&
      freqBase != null &&
      canOfferChangeFrequency(freqBase);

    // Option A: once switch has been shown on pre-2nd, menu is switch-only —
    // unless they explicitly ask to delay / space out (re-opens that lever).
    const afterSwitch = resolveOffersAfterSwitchShown({
      isBeforeSecondShipment,
      switchShown: hasSwitchBeenShown(cards),
      switchStillOfferable: isPreSecondSwitchStillOfferable({ cards }),
      explicitAsk: explicitAskReopen,
      canOfferDelay: delayEligible,
      canOfferChangeFrequency: freqEligible,
    });
    if (afterSwitch !== null) return afterSwitch;

    const all = getEligibleOffersForReason(
      cancelContext.eligibleOffersByReason,
      currentReason,
    );
    const eligible = all
      .filter((o) => !ideaExhaustedOffersRef.current.has(o))
      .filter((o) => {
        // Pre-2nd switch stays callable after a voice reply until declined.
        if (o === "switch_to_one_time" && isBeforeSecondShipment) {
          return isPreSecondSwitchStillOfferable({ cards });
        }
        return (shownOfferCounts.get(o) ?? 0) < maxPresentations(o);
      })
      .filter((o) => o !== "swap" || swapDefaultBundle.length >= 1)
      // Bundle is gated to price roots with a qualifying computation (see above),
      // for the same reason as the swap/freq filters: don't put a lever on the
      // table the widget can't render a real card for.
      .filter((o) => o !== "bundle" || bundleShowable)
      // changeFrequencyAvailable (from ManageSubscriptionSheet) only checks
      // that the interval fields are non-null — it doesn't validate them the
      // way `freqBase` does (count >= 1, unit in day|week|month|year). An
      // unmapped unit (e.g. Stay AI returning "MONTHS") would otherwise offer
      // this lever, buildFrequencyChoices would return [], and the card would
      // render with zero buttons — a dead end mid-cancellation. Same
      // precedent as the swap filter above: drop the lever rather than show
      // an empty card. Also drop when every 2–4× option would exceed the
      // 1–8 month cadence bound.
      .filter(
        (o) =>
          o !== "change_frequency" ||
          (freqBase != null && canOfferChangeFrequency(freqBase)),
      );

    // Fit policy: prefer levers that answer the recorded root. Empty fitting
    // falls back to eligible only for a first save attempt; after any decline
    // (confirm unlocked) — or for dislike without swap — the table stays empty.
    // Pre-2nd always keeps switch_to_one_time when eligible (real exit).
    return resolveAvailableOffersForSubReason({
      eligible,
      subReason: subReasonRef.current,
      isBeforeSecondShipment,
      offerDeclined: anyOfferDeclined || offerDeclinedRef.current,
    });
  }, [
    currentReason,
    cancelContext,
    shownOfferCounts,
    swapDefaultBundle.length,
    freqBase,
    bundleShowable,
    isSingleLineScope,
    isBeforeSecondShipment,
    ideaExhaustedVersion,
    anyOfferDeclined,
    cards,
    explicitAskReopen,
  ]);
  // Interactive pending only — voice-replied cards keep status=pending with an
  // answer set (chips hidden) and must not block "nothing left" / watchdog.
  const anyOfferPending = cards.some(
    (c) => c.kind !== "confirm" && c.status === "pending" && !c.answer,
  );
  // Nothing left to offer (either no eligible levers at all, or all shown) and
  // none currently on screen — graceful degradation straight to confirm.
  // Hold while swap-offer is still in flight: an empty menu may become ["swap"].
  const noOffersLeft =
    reasonSet &&
    remainingOffers.length === 0 &&
    !anyOfferPending &&
    !swapOfferAwaiting;
  // Before the 2nd shipment the customer CANNOT cancel — they can only delay,
  // change frequency, or switch to one-time — so confirm-cancel is never unlocked.
  // Otherwise the model may cancel once an offer was declined, or immediately when
  // there is nothing left to offer.
  const canConfirmCancel =
    !isBeforeSecondShipment &&
    reasonSet &&
    // Never unlock confirm while an unused swap is still on the table — declining
    // delay must not skip straight to cancel when a product swap is still owed.
    !remainingOffers.includes("swap") &&
    (anyOfferDeclined || noOffersLeft);

  const activeOffer = useMemo(
    () => ({
      currentReasonId: currentReason,
      // Read from the ref (set alongside reasonRef in the record_intake
      // handler, in the same render that flips currentReason) rather than
      // adding separate state — this recomputes exactly when currentReason
      // does, which is the only time it can change.
      currentSubReasonId: subReasonRef.current,
      availableOffers: remainingOffers,
      canConfirmCancel,
      retentionReopenedAfterSwitch: explicitAskReopen != null,
    }),
    [currentReason, remainingOffers, canConfirmCancel, explicitAskReopen],
  );
  activeOfferRef.current = activeOffer;

  // Analytics for offer cards mirrors the legacy CancelSubscriptionDialog's
  // retention events (subscription_retention_offer_shown / _declined). Each offer
  // kind maps to the same `offer` label the old flow used so the two surfaces are
  // comparable; the confirm-cancel card is not a retention offer and is excluded.
  const offerAnalyticsName = (kind: CardKind): string =>
    kind === "swap"
      ? "product_swap"
      : kind === "bundle"
        ? "size_up_bundle"
        : kind;
  const retentionOfferProps = (card: OfferCard) => ({
    productId: product.id,
    subscription_contract_id: subscriptionId ?? undefined,
    reason: reasonRef.current ?? undefined,
    offer: offerAnalyticsName(card.kind),
    ...(card.rePitch ? { re_pitch: true } : {}),
    ...(card.proposedWeeks != null
      ? { proposed_weeks: card.proposedWeeks.join(",") }
      : {}),
    ...(card.proposedMultipliers != null
      ? { proposed_multipliers: card.proposedMultipliers.join(",") }
      : {}),
    ...(card.kind === "bundle" && bundleOffer
      ? {
        saving_per_month: bundleOffer.savingPerMonth,
        new_interval_months: bundleOffer.newIntervalMonths,
        shipping_included: bundleOffer.shippingIncluded,
      }
      : {}),
    ...cancelTrackProps(),
  });

  // Fire subscription_retention_offer_shown once per offer card the moment it first
  // appears (model-driven or synthetic watchdog fallback), deduped by card id.
  const shownOfferIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!trackingShop) return;
    for (const card of cards) {
      if (card.kind === "confirm") continue;
      if (shownOfferIdsRef.current.has(card.id)) continue;
      shownOfferIdsRef.current.add(card.id);
      trackBuyAgainWidgetInteract(
        "subscription_retention_offer_shown",
        trackingShop,
        retentionOfferProps(card),
      );
      // Dedicated bundle-shown event so the new lever is directly queryable
      // without filtering the generic retention event down to its rows.
      if (card.kind === "bundle") {
        trackBuyAgainWidgetInteract(
          "subscription_bundle_shown",
          trackingShop,
          retentionOfferProps(card),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire on new cards only
  }, [cards, trackingShop]);

  // Safety net: a finished assistant turn with no next step (no offer_*/confirm
  // tool, no suggest_replies) must not strand the customer on a dead-end
  // sentence. Statement-only turns recover by synthesizing the first remaining
  // offer (or confirm when the menu is empty and cancel is unlocked). Clarifying
  // questions (text ending in "?") are left alone — EXCEPT when swap is on the
  // table and the model text-pitched a swap invitation without calling
  // offer_swap (those invitations are required to end in "?").
  //
  // Voice-replied cards keep status=pending with answer set (chips hidden) —
  // those must NOT block recovery, or a typed "cancel" + text-only close freezes.
  // Do NOT recover empty-text turns (e.g. record_reason): that races the
  // intake→save auto-continue and can stack a synthetic offer on the model's.
  //
  // When switch_to_one_time is the ONLY lever left, keep re-offering it after
  // every soft-close — even if the assistant message still carries earlier
  // offer_* tool parts (multi-step turns), which would otherwise make
  // hasActionTool bail and leave them with no buttons.
  useEffect(() => {
    if (isTyping || !reasonSet || success) return;
    // Don't unlock confirm / invent a dead-end while swap may still arrive.
    if (swapOfferAwaiting) return;
    const hasInteractivePending = cards.some(
      (c) => c.status === "pending" && !c.answer,
    );
    if (hasInteractivePending) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant") return;
    const parts = (last.parts ?? []) as Array<{ type: string; text?: string }>;
    const switchOnlyOnMenu =
      remainingOffers.length === 1 &&
      remainingOffers[0] === "switch_to_one_time";
    const hasActionTool = parts.some(
      (p) =>
        p.type === "tool-confirm_cancel" || p.type.startsWith("tool-offer_"),
    );
    // Prior offer_* parts on the same message are fine once those cards are
    // answered — only block when switch is NOT the sole remaining exit.
    if (hasActionTool && !switchOnlyOnMenu) return;
    if (parts.some((p) => p.type === "tool-suggest_replies")) return;
    const text = parts
      .filter((p) => p.type === "text" && p.text?.trim())
      .map((p) => p.text!.trim())
      .join(" ");
    if (!text) return;

    if (remainingOffers.length > 0) {
      // Pre-2nd: once switch has already been shown, a statement-only stall
      // should recover with switch (the exit) — not re-pitch delay/frequency.
      const switchAlreadyShown = cards.some(
        (c) => c.kind === "switch_to_one_time",
      );
      const kind =
        switchOnlyOnMenu ||
          (isBeforeSecondShipment &&
            switchAlreadyShown &&
            remainingOffers.includes("switch_to_one_time"))
          ? "switch_to_one_time"
          : remainingOffers[0]!;
      const canRender =
        (kind !== "swap" || swapDefaultBundle.length >= 1) &&
        (kind !== "bundle" || bundleOffer != null);
      if (!canRender) return;
      const isQuestion = /\?\s*$/.test(text);
      // Recover text-only swap pitches (they end in "?" by prompt design).
      // Other "?" turns are clarifying questions — leave them alone —
      // EXCEPT when switch is the only exit left (or pre-2nd switch re-offer).
      const recoveringSwitchExit =
        kind === "switch_to_one_time" &&
        (switchOnlyOnMenu ||
          (isBeforeSecondShipment && switchAlreadyShown));
      if (isQuestion && kind !== "swap" && !recoveringSwitchExit) return;
      setCards((prev) => {
        if (prev.some((c) => c.status === "pending" && !c.answer)) return prev;
        // One synthetic per assistant message id — if they already answered it,
        // do not mint another for the same soft-close (that emptied the menu
        // before the model turn). A new assistant stall gets a new message id.
        const id = `synthetic-offer-${kind}-${last.id}`;
        if (prev.some((c) => c.id === id)) return prev;
        const bundle: OfferCard["bundle"] | undefined =
          kind === "swap"
            ? {
              products: swapDefaultBundle,
              comboTotal: swapQuery.data?.comboTotal ?? null,
            }
            : undefined;
        injectSyntheticToolPart(last.id, id, kind);
        return [
          ...prev,
          {
            id,
            kind,
            status: "pending",
            synthetic: true,
            ...(bundle ? { bundle } : {}),
          },
        ];
      });
      return;
    }

    if (!text) return;
    if (isBeforeSecondShipment || !canConfirmCancel) return;
    if (cards.some((c) => c.kind === "confirm")) return;
    setCards((prev) => {
      if (prev.some((c) => c.kind === "confirm")) return prev;
      const id = `synthetic-confirm-${last.id}`;
      injectSyntheticToolPart(last.id, id, "confirm");
      return [
        ...prev,
        {
          id,
          kind: "confirm",
          status: "pending",
          synthetic: true,
        },
      ];
    });
  }, [
    isTyping,
    messages,
    cards,
    canConfirmCancel,
    reasonSet,
    success,
    isBeforeSecondShipment,
    remainingOffers,
    swapDefaultBundle,
    bundleOffer,
    swapQuery.data?.comboTotal,
    swapOfferAwaiting,
  ]);

  // Identity of the conversation currently held in state, so reopening the sheet
  // can tell "they came back to the SAME conversation" from "this is a new one".
  const conversationKeyRef = useRef<string | null>(null);

  // Closing the sheet no longer resets anything, and no longer stops the stream.
  //
  // The component stays MOUNTED across close/reopen — `open` is only a prop — so
  // the transcript, cards and intake refs survive on their own. Resuming is simply
  // the act of not wiping them. And letting an in-flight response finish while the
  // sheet is closed means they come back to a complete answer rather than a severed
  // sentence; the tokens were already being generated either way, and record_intake
  // still lands, which is the richest churn signal we collect from someone who
  // abandons the chat. See cancel-chat-resume.ts for when a reset IS correct.
  useEffect(() => {
    if (!open) return;
    const currentKey = conversationKey(subscriptionId, product.id);
    const reset = shouldResetConversation({
      conversationKey: conversationKeyRef.current,
      currentKey,
      success: successRef.current,
      fatal: firedErrorRef.current,
    });
    if (reset) {
      conversationKeyRef.current = currentKey;
      // A 2+ line contract on/after the 2nd shipment opens on the which-product
      // question (see needsProductChoice) with an EMPTY transcript — the motivation
      // greeting is scoped to whatever they pick and only fires once they have.
      // Single-line and multi-line before the 2nd shipment seed the greeting
      // immediately (whole-contract subject for the latter).
      setProductChoice(null);
      setMessages(
        isMultiLine && !isBeforeSecondShipment
          ? []
          : [
            greetingMessage({
              subjectText: subject.text,
              firstName: customerName?.trim().split(/\s+/)[0] || null,
              replyOptions: getMotivationChips(
                product.title,
                siblingProductTitles ?? [],
              ),
            }),
          ],
      );
      setInput("");
      setBundleDatePickerFor(null);
      setBundleBillingDate(null);
      setReasonSet(false);
      setSaveDeferredForSwap(false);
      pendingSaveContinueRef.current = false;
      saveResumeStartedRef.current = false;
      setCurrentReason(null);
      setCards([]);
      voiceRepliedToolCallIdsRef.current.clear();
      ideaExhaustedOffersRef.current = new Set();
      setIdeaExhaustedVersion(0);
      setSuccess(null);
      successRef.current = null;
      swapCompletedRef.current = false;
      setPending(false);
      whyBoughtRef.current = "";
      whyDetailRef.current = "";
      rawMotivationRef.current = null;
      rawCancelWordsRef.current = [];
      openingChipsRef.current = [];
      motivationShownRef.current = false;
      reasonRef.current = null;
      subReasonRef.current = null;
      otherTextRef.current = "";
      offerDeclinedRef.current = false;
      firedErrorRef.current = false;
      shownOfferIdsRef.current = new Set();
      conversationIdRef.current = createCancelAttemptId();
    }
    // Fires on EVERY open, resumed or not — keeping it inside the reset above would
    // silently stop counting reopens and under-report the funnel.
    if (trackingShop) {
      trackBuyAgainWidgetInteract("subscription_cancel_open", trackingShop, {
        productId: product.id,
        subscription_contract_id: subscriptionId ?? undefined,
        resumed: !reset,
        ...cancelTrackProps(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- decide on open toggle only
  }, [open]);

  // Auto-scroll to the newest message / card. `open` is in here for the RESUMED
  // case: the messages haven't changed, so without it a resumed conversation would
  // reopen scrolled to the top of the transcript instead of where they left off.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isTyping, cards, success, open]);

  // Keep the conversation pinned to the bottom while the keyboard resizes the
  // sheet, so the newest message and the composer stay visible together.
  useEffect(() => {
    if (keyboardOpen) {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [keyboardOpen]);

  // Pin the sheet to the visible viewport while the keyboard is up.
  //
  // This MUST be imperative. The widget's Tailwind runs with `important: true`,
  // so the sheet's own `h-full` compiles to `height: 100% !important` — and a
  // React/Preact inline `style` prop emits a declaration with no priority, which
  // loses to it. The geometry was being computed correctly and thrown away by the
  // cascade, which is why the sheet never shrank and why five earlier widget-side
  // fixes all failed the same way and looked environmental. See keyboard-geometry.ts.
  //
  // The sheet element is found by walking up from the transcript rather than via a
  // forwarded ref: on desktop the same body renders inside a Dialog, so `closest`
  // returns null there and the geometry is correctly left alone.
  useEffect(() => {
    const sheet = scrollRef.current?.closest<HTMLElement>(
      '[data-slot="sheet-content"]',
    );
    if (!sheet) return;
    applyKeyboardGeometry(sheet, { keyboardOpen, offsetTop, visibleHeight });
  }, [keyboardOpen, offsetTop, visibleHeight, open]);

  const reasonId = reasonRef.current;
  const meta = (): CancelRetentionMeta => {
    // Prefer the customer's verbatim cancel answers over the model's paraphrase
    // (support email "In their words" must quote them, not an AI rewrite).
    const rawCancel = rawCancelWordsRef.current
      .map((w) => w.trim())
      .filter(Boolean)
      .join(" — ");
    const otherText = otherTextRef.current.trim();
    const whyDetail =
      rawCancel || otherText || whyDetailRef.current.trim();
    return {
      whyBought:
        rawMotivationRef.current?.text.trim() || whyBoughtRef.current.trim(),
      whyDetail,
      cancelReason: reasonId ? getCancelReasonLabel(reasonId) : "",
      cancelReasonOther:
        reasonId === "other" ? otherText || undefined : undefined,
      cancelReasonId: reasonId as CancelSubscriptionReasonId,
      conversationId: conversationIdRef.current || undefined,
    };
  };

  const cardToolName = (kind: CardKind) =>
    kind === "confirm" ? "confirm_cancel" : `offer_${kind}`;

  /** Inject a durable tool-shaped part so synthetic accept/decline persist for admin. */
  const injectSyntheticToolPart = (
    messageId: string,
    cardId: string,
    kind: CardKind,
  ) => {
    const toolType = `tool-${cardToolName(kind)}` as `tool-${string}`;
    setMessagesRef.current?.((prev) =>
      prev.map((m) => {
        if (m.id !== messageId) return m;
        const parts = m.parts ?? [];
        if (
          parts.some(
            (p) => (p as { toolCallId?: string }).toolCallId === cardId,
          )
        ) {
          return m;
        }
        return {
          ...m,
          parts: [
            ...parts,
            {
              type: toolType,
              toolCallId: cardId,
              state: "input-available" as const,
              input: {},
            },
          ],
        };
      }),
    );
  };

  const stampToolPartOutput = (card: OfferCard, output: unknown) => {
    const toolType = `tool-${cardToolName(card.kind)}` as `tool-${string}`;
    setMessagesRef.current?.((prev) =>
      prev.map((m) => ({
        ...m,
        parts: (m.parts ?? []).map((p) => {
          const part = p as {
            type?: string;
            toolCallId?: string;
            state?: string;
            output?: unknown;
          };
          if (part.type === toolType && part.toolCallId === card.id) {
            return {
              ...p,
              state: "output-available",
              output,
            } as typeof p;
          }
          return p;
        }),
      })),
    );
  };

  /**
   * Option labels + swap products the card showed — stamped on the tool result
   * so the admin transcript can mirror the customer UI.
   */
  const offerTranscriptExtras = (card: OfferCard) => {
    const declineLabel =
      card.kind === "switch_to_one_time" && isBeforeSecondShipment
        ? "Keep subscription"
        : card.kind === "confirm"
          ? "Keep subscription"
          : "Not now";
    let shownOptions: string[] = [declineLabel];
    if (card.kind === "delay") {
      const choices = buildDelayChoices({
        weeks: card.proposedWeeks ?? null,
        presets: cancelContext.delayPresets,
        nextBillingDate: product.subscription_next_billing_date ?? null,
      });
      shownOptions = [...choices.map((c) => c.label), declineLabel];
    } else if (card.kind === "change_frequency") {
      const choices = buildFrequencyChoices({
        multipliers: card.proposedMultipliers ?? [2, 3],
        base: freqBase,
        subject,
        contractPerDeliveryTotal: cancelContext.contractPerDeliveryTotal,
        cadence: cancelContext.cadence,
      });
      shownOptions = [...choices.map((c) => c.label), declineLabel];
    } else if (card.kind === "swap") {
      shownOptions = ["Swap my subscription", declineLabel];
    } else if (card.kind === "bundle" && bundleOffer) {
      const copy = buildBundleCardCopy(
        bundleOffer,
        cancelContext.currencySymbol,
      );
      shownOptions = [copy.acceptLabel, declineLabel];
    } else if (card.kind === "switch_to_one_time") {
      const diff = cancelContext.estimatedDiff ?? 0;
      shownOptions = cancelContext.cancelAfterNextDeliveryAvailable
        ? [saveLabel(product.subscription_next_billing_date ?? ""), cancelTodayLabel(diff), declineLabel]
        : [
          diff > 0
            ? `Switch to one-time (+$${formatPrice(diff)})`
            : "Switch to one-time",
          declineLabel,
        ];
    } else if (card.kind === "confirm") {
      shownOptions = isSingleLineScope
        ? [`Cancel just ${activeProduct.title}`, declineLabel]
        : ["Confirm cancellation", declineLabel];
    }
    const products =
      card.kind === "swap" && card.bundle?.products?.length
        ? serializeOfferProducts(card.bundle.products)
        : undefined;
    return {
      shownOptions,
      ...(products?.length ? { products } : {}),
    };
  };

  // Resolve a card (accepted / declined), record the answer bubble, and report
  // the outcome back to the model. Synthetics / voice-replied cards patch the
  // durable tool part in the message stream (admin transcript); live model
  // tools use addToolResult.
  const resolveCard = (
    card: OfferCard,
    status: "accepted" | "declined",
    answer: string,
    output: unknown,
  ) => {
    setCards((prev) =>
      prev.map((c) => (c.id === card.id ? { ...c, status, answer } : c)),
    );
    const base =
      output && typeof output === "object"
        ? (output as Record<string, unknown>)
        : {};
    const merged = { ...base, ...offerTranscriptExtras(card) };
    if (
      card.synthetic ||
      voiceRepliedToolCallIdsRef.current.has(card.id)
    ) {
      stampToolPartOutput(card, merged);
      return;
    }
    addToolResultRef.current?.({
      tool: cardToolName(card.kind),
      toolCallId: card.id,
      output: merged,
    });
  };

  // --- raw executors (no success/pending bookkeeping) --------------------
  // conversationId + offerTaken travel with the accept so app-backend can
  // stamp this conversation `saved` — only chat accepts carry this; the
  // sheet's own (non-chat) delay/change-frequency controls omit it.
  const rawDelay = (date: string, weeks: number) =>
    cancelChatTestMode
      ? Promise.resolve()
      : onDelayDateRef.current?.(date, weeks, {
        conversationId: conversationIdRef.current,
        offerTaken: "delay",
      });
  const rawChangeFreq = (interval: number, unit: StayAiIntervalUnit) =>
    cancelChatTestMode
      ? Promise.resolve()
      : onChangeFrequencyToRef.current?.(interval, unit, {
        conversationId: conversationIdRef.current,
        offerTaken: "change_frequency",
      });
  // Apply the size-up bundle: the backend runs the two Stay AI writes (swap-product
  // then change-frequency) synchronously and updates the local DB. Called directly
  // (like rawSwap) rather than through a sheet prop. NOTE: the swap's immediacy
  // (applies now vs next billing) is UNVERIFIED pending a staging test — we build
  // and message as if it applies immediately, mirroring change-frequency.
  //
  // Stay AI `isDiscounted: true` means "use this exact price" — so we send the
  // promo-folded net (`targetNetPrice`) as `price`, and the S&S catalog
  // (`targetUnitPrice`) as `unitPrice` for local unit_price storage.
  const rawBundle = async (offer: SizeUpBundle, nextBillingDate?: string) => {
    if (cancelChatTestMode) return;
    if (subscriptionId == null) return;
    await applySizeUpBundleStayAiSubscription(
      subscriptionId,
      {
        variantId: offer.targetVariantId,
        price: offer.targetNetPrice,
        unitPrice: offer.targetUnitPrice,
        quantity: offer.quantity,
        interval: offer.newIntervalMonths,
        intervalUnit: "month",
        // The billing/shipment date the customer chose on accept — the backend
        // bills the new plan from here instead of the interval-derived default.
        nextBillingDate: nextBillingDate || undefined,
      },
      { conversationId: conversationIdRef.current, offerTaken: "bundle" },
    );
    void queryClient.invalidateQueries({
      queryKey: ["purchased-product-detail", product.id],
    });
    if (trackingShop) {
      trackBuyAgainWidgetInteract("subscription_bundle_accepted", trackingShop, {
        productId: product.id,
        subscription_contract_id: subscriptionId ?? undefined,
        reason: reasonId ?? undefined,
        sub_reason: subReasonRef.current ?? undefined,
        target_variant_id: offer.targetVariantId,
        new_interval_months: offer.newIntervalMonths,
        saving_per_month: offer.savingPerMonth,
        shipping_included: offer.shippingIncluded,
        next_billing_date: nextBillingDate || undefined,
        ...cancelTrackProps(),
      });
    }
  };
  const rawSwitch = () =>
    cancelChatTestMode
      ? Promise.resolve()
      : onSubmit({
        ...meta(),
        cancelAction: "switch_to_one_time",
        offerDeclined: false,
      });
  // Keep the next delivery at the subscription price, cancel right after it.
  // The waived fee is the figure this card showed.
  const rawSchedule = () =>
    cancelChatTestMode
      ? Promise.resolve()
      : onSubmit({
        ...meta(),
        cancelAction: "cancel_after_next_delivery",
        offerDeclined: false,
        feeAmountWaived: cancelContext.estimatedDiff ?? null,
      });
  /** The bundle a swap CARD actually offered (API default; AI pick disabled). */
  const bundleOfCard = (card: OfferCard) =>
    card.bundle?.products?.length
      ? card.bundle
      : {
        products: swapDefaultBundle,
        comboTotal: swapQuery.data?.comboTotal ?? null,
      };

  /** Products shown on the LEFT of the swap card (what they're swapping from). */
  const swapCurrentProducts = useMemo(() => {
    const lines = isSingleLineScope ? [activeProduct] : contractLines;
    return lines.map((line) => ({
      title: line.title,
      imageUrl: line.image_url,
      variantTitle: line.variant_title ?? null,
    }));
  }, [isSingleLineScope, activeProduct, contractLines]);

  const rawSwap = async (card: OfferCard) => {
    if (cancelChatTestMode) return;
    const bundle = bundleOfCard(card);
    if (subscriptionId == null || bundle.products.length === 0) return;
    const m = meta();
    const result = await submitSwapRequest(
      subscriptionId,
      bundle.products.map((c) => ({
        id: c.id,
        title: c.title,
        variantId: c.variantId ?? null,
        variantTitle: c.variantTitle ?? null,
        price: c.price ?? null,
      })),
      {
        sourceProductId: activeProduct.id,
        conversationId: conversationIdRef.current || undefined,
        sourceProductTitle: activeProduct.title,
        comboTotal: bundle.comboTotal,
        currentPrice,
        cancelReasonLabel: m.cancelReason || undefined,
        whyBought: m.whyBought || undefined,
        whyDetail: m.whyDetail || undefined,
        customerEmail,
        customerName,
      },
    );
    swapCompletedRef.current = result.completed;
    if (result.completed) {
      void queryClient.invalidateQueries({
        queryKey: ["purchased-product-detail", product.id],
      });
      void queryClient.invalidateQueries({
        queryKey: ["purchased-products"],
      });
    } else {
      void queryClient.invalidateQueries({
        queryKey: swapStatusQueryKey(subscriptionId),
      });
    }
    if (trackingShop) {
      trackBuyAgainWidgetInteract("subscription_swap_accepted", trackingShop, {
        productId: product.id,
        subscription_contract_id: subscriptionId ?? undefined,
        reason: reasonId ?? undefined,
        swap_product_ids: bundle.products.map((c) => c.id),
        completed: result.completed,
        // ai_composed: re-enable when AI productIds composition is turned back on
        ...cancelTrackProps(),
      });
    }
  };
  const rawConfirm = () =>
    cancelChatTestMode
      ? Promise.resolve()
      : onSubmit({
        ...meta(),
        cancelAction: "cancel",
        offerDeclined: offerDeclinedRef.current,
      });
  // Terminal single-line removal. Deliberately NOT onSubmit/rawConfirm: that
  // cancels the WHOLE contract via Stay AI. This calls remove-lines for the
  // chosen product and stamps the conversation partial_cancelled — the other
  // lines never stop. The removed line's own id + title travel with it (not
  // `product`'s), and the retained titles for success copy.
  const rawPartialCancel = async () => {
    if (cancelChatTestMode) return;
    if (subscriptionId == null) return;
    const m = meta();
    await submitPartialCancelRequest({
      subscriptionId,
      productId: activeProduct.id,
      productTitle: activeProduct.title,
      retainedProductTitles: retainedTitles,
      customerEmail,
      customerName,
      whyBought: m.whyBought,
      whyDetail: m.whyDetail,
      cancelReasonId: m.cancelReasonId,
      cancelReasonLabel: m.cancelReason,
      cancelReasonOther: m.cancelReasonOther,
      conversationId: m.conversationId,
    });
    // Same event the full cancel fires (from ManageSubscriptionSheet), but stamped
    // `partial_cancel` and carrying the removed line's id — so full-vs-partial is
    // countable off ONE event, and a partial never reads as a whole-contract
    // cancel in the funnel. Full cancels keep cancel_action: "cancel".
    if (trackingShop) {
      trackBuyAgainWidgetInteract(
        "subscription_cancel_confirmed",
        trackingShop,
        {
          productId: activeProduct.id,
          subscription_contract_id: subscriptionId,
          reason: m.cancelReasonId ?? m.cancelReason,
          cancel_action: "partial_cancel",
          offer_declined: offerDeclinedRef.current,
          ...cancelTrackProps(),
        },
      );
    }
  };

  handleAcceptToolRef.current = async (toolCall) => {
    const name = toolCall.toolName;
    const input = (toolCall.input ?? {}) as Record<string, unknown>;
    const report = (output: unknown) => {
      addToolResultRef.current?.({
        tool: name,
        toolCallId: toolCall.toolCallId,
        output,
      });
    };

    if (name === "accept_confirm_cancel") {
      const pendingCard = [...cardsRef.current]
        .reverse()
        .find((c) => c.kind === "confirm" && c.status === "pending");
      if (!pendingCard) {
        report({ ok: false, error: "No pending confirm_cancel to accept" });
        return;
      }
      const spoken = pendingCard.answer?.trim();
      try {
        if (isSingleLineScope) {
          await acceptOffer(
            pendingCard,
            rawPartialCancel,
            "partial_cancelled",
            `Cancel just ${activeProduct.title}`,
            spoken ? { spokenText: spoken } : undefined,
          );
        } else {
          await acceptOffer(
            pendingCard,
            rawConfirm,
            "cancelled",
            "Confirm cancellation",
            spoken ? { spokenText: spoken } : undefined,
          );
        }
        report({ ok: true });
      } catch (err) {
        report({
          ok: false,
          error: err instanceof Error ? err.message : "confirm cancel failed",
        });
      }
      return;
    }

    if (name === "accept_delay") {
      const weeks = typeof input.weeks === "number" ? input.weeks : NaN;
      if (!Number.isInteger(weeks) || weeks < 1 || weeks > 12) {
        report({ ok: false, error: "Invalid weeks" });
        return;
      }
      const nextBillingDate = product.subscription_next_billing_date;
      if (!nextBillingDate) {
        report({ ok: false, error: "Missing next billing date" });
        return;
      }
      const date = addBillingInterval(nextBillingDate, "week", weeks);
      await onDelayDateRef.current?.(date, weeks, {
        conversationId: conversationIdRef.current,
        offerTaken: "delay",
      });
      report({ ok: true });
      return;
    }

    if (name === "accept_change_frequency") {
      const interval = typeof input.interval === "number" ? input.interval : NaN;
      const unitRaw =
        typeof input.intervalUnit === "string" ? input.intervalUnit : "month";
      if (!Number.isInteger(interval) || interval < 1) {
        report({ ok: false, error: "Invalid interval" });
        return;
      }
      const normalized = normalizeFrequencyToMonths(interval, unitRaw);
      if (!normalized) {
        report({ ok: false, error: "Cadence out of bounds" });
        return;
      }
      await onChangeFrequencyToRef.current?.(
        normalized.interval,
        normalized.unit,
        {
          conversationId: conversationIdRef.current,
          offerTaken: "change_frequency",
        },
      );
      report({ ok: true });
      return;
    }

    if (name === "accept_swap") {
      const pending = cards.find(
        (c) => c.kind === "swap" && c.status === "pending",
      );
      if (!pending) {
        report({ ok: false, error: "No pending swap offer" });
        return;
      }
      await rawSwap(pending);
      report({ ok: true });
      return;
    }

    if (name === "accept_switch_to_one_time") {
      await rawSwitch();
      report({ ok: true });
      return;
    }

    if (name === "accept_cancel_after_next_delivery") {
      const pendingCard = [...cardsRef.current]
        .reverse()
        .find((c) => c.kind === "switch_to_one_time" && c.status === "pending");
      if (!pendingCard || !cancelContext.cancelAfterNextDeliveryAvailable) {
        report({ ok: false, error: "No pending cancel-after-next-delivery offer" });
        return;
      }
      await acceptOffer(
        pendingCard,
        rawSchedule,
        "scheduled",
        saveLabel(product.subscription_next_billing_date ?? ""),
        { result: { cancelAction: "cancel_after_next_delivery" } },
      );
      report({ ok: true });
      return;
    }

    if (name === "accept_bundle") {
      const pending = cards.find(
        (c) => c.kind === "bundle" && c.status === "pending",
      );
      if (!pending || !bundleOffer) {
        report({ ok: false, error: "No pending bundle offer" });
        return;
      }
      const defaultBillingDate =
        product.subscription_next_billing_date?.slice(0, 10) ?? "";
      await rawBundle(bundleOffer, defaultBillingDate);
      report({ ok: true });
      return;
    }

    report({ ok: false, error: `Unknown accept tool: ${name}` });
  };

  const acceptOffer = async (
    card: OfferCard,
    rawFn: () => Promise<unknown> | unknown,
    successKind: SuccessKind,
    answerLabel: string,
    opts?: {
      /** Raw typed text — shown in the transcript instead of the chip label. */
      spokenText?: string;
      /** Extra fields on the offer_* tool result (weeks, interval, spokenText). */
      result?: Record<string, unknown>;
    },
  ) => {
    if (pending) return;
    const displayAnswer = opts?.spokenText?.trim() || answerLabel;
    // Show their words immediately (hides chips) so the wait isn't a blank stall.
    setCards((prev) =>
      prev.map((c) => (c.id === card.id ? { ...c, answer: displayAnswer } : c)),
    );
    setPending(true);
    setSubmittingLabel(answerLabel);
    try {
      await rawFn();
      successRef.current = successKind;
      setSuccess(successKind);
      resolveCard(card, "accepted", displayAnswer, {
        accepted: true,
        ...(opts?.spokenText ? { spokenText: opts.spokenText.trim() } : {}),
        ...(opts?.result ?? {}),
      });
      if (trackingShop && opts?.spokenText) {
        trackBuyAgainWidgetInteract(
          "subscription_retention_offer_accepted",
          trackingShop,
          {
            ...retentionOfferProps(card),
            spoken_text: opts.spokenText.trim(),
            via: "typed",
            ...(opts.result ?? {}),
          },
        );
      }
    } catch (error) {
      toastSubscriptionApiError(error);
    } finally {
      setPending(false);
      setSubmittingLabel(null);
    }
  };

  // `reaction` is the customer's typed rejection (reported to the model + tracked);
  // `label` overrides the transcript bubble text so it matches the button they
  // tapped (e.g. "Keep subscription" rather than the default "Not now").
  const declineOffer = (
    card: OfferCard,
    opts?: { reaction?: string; label?: string },
  ) => {
    offerDeclinedRef.current = true;
    const trimmed = opts?.reaction?.trim();
    if (trackingShop) {
      trackBuyAgainWidgetInteract(
        "subscription_retention_offer_declined",
        trackingShop,
        {
          ...retentionOfferProps(card),
          ...(trimmed ? { reaction: trimmed } : {}),
        },
      );
    }
    // Explicit-ask reopen is one-shot: declining delay/freq returns to switch-only.
    if (card.kind === "delay" || card.kind === "change_frequency") {
      setExplicitAskReopen(null);
    }
    // Idea decline → drop this lever from the menu (no 2nd presentation).
    // Sync activeOfferRef before addToolResult so the auto-continue POST sees
    // the shrunk menu (and confirm only once nothing remains).
    if (
      card.kind !== "confirm" &&
      shouldExhaustOfferOnDecline({
        declined: card.kind,
        availableOffers: remainingOffers,
      })
    ) {
      ideaExhaustedOffersRef.current.add(card.kind);
      setIdeaExhaustedVersion((v) => v + 1);
      const nextAvailable = remainingOffers.filter((o) => o !== card.kind);
      activeOfferRef.current = {
        currentReasonId: currentReason,
        currentSubReasonId: subReasonRef.current,
        availableOffers: nextAvailable,
        canConfirmCancel:
          !isBeforeSecondShipment && !nextAvailable.includes("swap"),
        retentionReopenedAfterSwitch: false,
      };
    }
    // Persist the visible answer (button label or typed reaction) so admin
    // review can show "Not now" / "Keep subscription", not just "Declined".
    const label = opts?.label ?? trimmed ?? "Not now";
    resolveCard(card, "declined", label, {
      accepted: false,
      label,
      ...(trimmed ? { reaction: trimmed } : {}),
    });
  };

  handleDeclineToolRef.current = async (toolCall) => {
    const name = toolCall.toolName;
    const report = (output: unknown) => {
      addToolResultRef.current?.({
        tool: name,
        toolCallId: toolCall.toolCallId,
        output,
      });
    };

    if (name === "decline_confirm_cancel") {
      try {
        const pendingCard = [...cardsRef.current]
          .reverse()
          .find((c) => c.kind === "confirm" && c.status === "pending");
        if (!pendingCard) {
          report({
            ok: false,
            error: "No pending confirm_cancel to decline",
          });
          return;
        }
        const reaction = pendingCard.answer?.trim();
        declineConfirm(pendingCard);
        report({ declined: true, ...(reaction ? { reaction } : {}) });
      } catch (err) {
        report({
          ok: false,
          error: err instanceof Error ? err.message : "decline confirm failed",
        });
      }
      return;
    }

    const lever = name.slice("decline_".length);
    try {
      const pendingCard = [...cardsRef.current]
        .reverse()
        .find((c) => c.kind === lever && c.status === "pending");
      if (!pendingCard) {
        report({ ok: false, error: `No pending ${lever} offer to decline` });
        return;
      }
      const reaction = pendingCard.answer?.trim();
      declineOffer(pendingCard, {
        ...(reaction ? { reaction, label: reaction } : {}),
      });
      report({ declined: true, ...(reaction ? { reaction } : {}) });
    } catch (err) {
      report({
        ok: false,
        error: err instanceof Error ? err.message : "decline failed",
      });
    }
  };

  const declineConfirm = (card: OfferCard) => {
    resolveCard(card, "declined", "Keep subscription", {
      confirmed: false,
      label: "Keep subscription",
    });
    onOpenChange(false);
  };

  const send = (
    text: string,
    // How the answer was given. Comes from the CALL SITE — a chip's onClick knows it
    // was a tap — never from matching the text against the chip list; see
    // cancel-chat-intake.ts.
    origin: { source: MotivationSource; chipIndex: number | null } = {
      source: "typed",
      chipIndex: null,
    },
  ) => {
    const trimmed = text.trim();
    if (!trimmed || chatBusy || pending) return;
    setInput("");
    // The FIRST user turn is the answer to "what drew you to X to begin with?" —
    // the app's own greeting, so no model call has happened yet. Record it here,
    // verbatim, rather than waiting for the model's record_intake: that way the
    // answer survives even when they abandon the chat before the model gets to it,
    // and a tapped chip stays distinguishable from typed text.
    if (!messages.some(isIntakeUserTurn)) {
      const answer = {
        text: trimmed,
        source: origin.source,
        chipIndex: origin.chipIndex,
        chipsShown: openingChipsRef.current,
      };
      rawMotivationRef.current = answer;
      if (trackingShop) {
        trackBuyAgainWidgetInteract(
          "subscription_cancel_motivation_answered",
          trackingShop,
          {
            productId: product.id,
            subscription_contract_id: subscriptionId ?? undefined,
            ...motivationEventProps(answer),
            ...cancelTrackProps(),
          },
        );
      }
    } else if (!reasonRef.current) {
      // Intake follow-ups after motivation, before record_reason — keep their
      // exact words for why_detail (support email + cancel payload).
      rawCancelWordsRef.current = [...rawCancelWordsRef.current, trimmed];
    }

    // Pending offer_* cards defer their tool result until the customer acts.
    // Chip declines call addToolResult only and rely on sendAutomaticallyWhen.
    // Typed replies must do the same: addToolResult is async and updates the
    // message parts before auto-continue. Calling sendMessage in the same tick
    // POSTs the unfinished tool call → AI_MissingToolResultsError on the server.
    let pendingOffer: OfferCard | undefined;
    for (let i = cards.length - 1; i >= 0; i--) {
      const c = cards[i];
      if (
        c.status === "pending" &&
        !c.answer &&
        !voiceRepliedToolCallIdsRef.current.has(c.id) &&
        // Watchdog synthetic switch cards are included — they have no server
        // tool call, but typing on them must still reach the model with switch
        // back on the menu (see synthetic branch below). Synthetic confirm is
        // excluded (no deferred server tool); model confirm_cancel is included
        // so typed "cancel" resolves via { replied: true } instead of crashing.
        (!c.synthetic || c.kind === "switch_to_one_time")
      ) {
        pendingOffer = c;
        break;
      }
    }
    if (pendingOffer) {
      // Explicit delay/freq ask on a pending switch card: do not treat it as a
      // switch soft-decline — re-open that lever and continue the turn.
      const retentionAsk = detectExplicitRetentionAsk(trimmed);
      if (
        retentionAsk &&
        pendingOffer.kind === "switch_to_one_time" &&
        isBeforeSecondShipment &&
        hasSwitchBeenShown(cards)
      ) {
        const reasonId = currentReason ?? reasonRef.current;
        const reasonEligible = reasonId
          ? getEligibleOffersForReason(
            cancelContext.eligibleOffersByReason,
            reasonId,
          )
          : [];
        const reopenMenu =
          resolveOffersAfterSwitchShown({
            isBeforeSecondShipment: true,
            switchShown: true,
            switchStillOfferable: true,
            explicitAsk: retentionAsk,
            canOfferDelay: reasonEligible.includes("delay"),
            canOfferChangeFrequency:
              reasonEligible.includes("change_frequency") &&
              freqBase != null &&
              canOfferChangeFrequency(freqBase),
          }) ?? [];
        if (reopenMenu.includes(retentionAsk)) {
          voiceRepliedToolCallIdsRef.current.add(pendingOffer.id);
          setExplicitAskReopen(retentionAsk);
          setCards((prev) =>
            prev.map((c) =>
              c.id === pendingOffer!.id ? { ...c, answer: trimmed } : c,
            ),
          );
          activeOfferRef.current = {
            currentReasonId: currentReason ?? reasonRef.current,
            currentSubReasonId: subReasonRef.current,
            availableOffers: reopenMenu,
            canConfirmCancel: false,
            retentionReopenedAfterSwitch: true,
          };
          if (pendingOffer.synthetic) {
            void sendMessage({ text: trimmed });
          } else {
            void addToolResultRef.current?.({
              tool: cardToolName(pendingOffer.kind),
              toolCallId: pendingOffer.id,
              output: { replied: true, text: trimmed },
            });
          }
          return;
        }
        // Ask wasn't eligible for this reason — fall through to normal switch reply.
      }

      voiceRepliedToolCallIdsRef.current.add(pendingOffer.id);
      setCards((prev) =>
        prev.map((c) =>
          c.id === pendingOffer!.id ? { ...c, answer: trimmed } : c,
        ),
      );
      // Synthetic switch (watchdog) has no tool call in the transcript — resolve
      // via a normal user turn with switch kept on the menu so the model can
      // call offer_switch_to_one_time (server forces a tool when switch-only).
      if (pendingOffer.synthetic && pendingOffer.kind === "switch_to_one_time") {
        activeOfferRef.current = {
          currentReasonId: currentReason ?? reasonRef.current,
          currentSubReasonId: subReasonRef.current,
          availableOffers: ["switch_to_one_time"],
          canConfirmCancel: false,
          retentionReopenedAfterSwitch: false,
        };
        void sendMessage({ text: trimmed });
        return;
      }
      // Mark answered and let the model continue. Pre-2nd switch stays on the
      // menu after a voice reply (see isPreSecondSwitchStillOfferable).
      void addToolResultRef.current?.({
        tool: cardToolName(pendingOffer.kind),
        toolCallId: pendingOffer.id,
        output: {
          replied: true,
          text: trimmed,
          ...offerTranscriptExtras(pendingOffer),
        },
      });
      return;
    }

    // Free-text turn (no pending card): re-open delay/freq on explicit ask after
    // switch was shown; cancel/fee phrasing leaves switch-only alone.
    const retentionAsk = detectExplicitRetentionAsk(trimmed);
    if (
      retentionAsk &&
      isBeforeSecondShipment &&
      hasSwitchBeenShown(cards)
    ) {
      const reasonId = currentReason ?? reasonRef.current;
      const reasonEligible = reasonId
        ? getEligibleOffersForReason(
          cancelContext.eligibleOffersByReason,
          reasonId,
        )
        : [];
      const reopenMenu =
        resolveOffersAfterSwitchShown({
          isBeforeSecondShipment: true,
          switchShown: true,
          switchStillOfferable: isPreSecondSwitchStillOfferable({ cards }),
          explicitAsk: retentionAsk,
          canOfferDelay: reasonEligible.includes("delay"),
          canOfferChangeFrequency:
            reasonEligible.includes("change_frequency") &&
            freqBase != null &&
            canOfferChangeFrequency(freqBase),
        }) ?? [];
      if (reopenMenu.includes(retentionAsk)) {
        setExplicitAskReopen(retentionAsk);
        activeOfferRef.current = {
          currentReasonId: currentReason ?? reasonRef.current,
          currentSubReasonId: subReasonRef.current,
          availableOffers: reopenMenu,
          canConfirmCancel: false,
          retentionReopenedAfterSwitch: true,
        };
      }
    } else if (
      isBeforeSecondShipment &&
      hasSwitchBeenShown(cards) &&
      retentionAsk == null &&
      /\b(cancel|don'?t want to pay|just cancel)\b/i.test(trimmed)
    ) {
      // Back to cancel insistence — drop a prior reopen so switch-only force returns.
      setExplicitAskReopen(null);
    }

    void sendMessage({ text: trimmed });
  };

  // They answered "which product would you like to cancel?". This is the one
  // decision the app makes without the model: it re-scopes the whole conversation
  // and only THEN fires the (now scoped) motivation greeting. Seeding that greeting
  // here — rather than letting the reset effect do it — is why the opener can name
  // the exact line they picked ("what drew you to A?") instead of the whole
  // contract. "Cancel everything" seeds today's whole-contract greeting, unchanged.
  const chooseProduct = (
    choice:
      | { kind: "single"; product: ManageSubscriptionSheetProduct }
      | { kind: "everything" },
    /** The chip's exact label, echoed back as the user's answer bubble. */
    answerLabel: string,
  ) => {
    if (productChoice !== null) return;
    setProductChoice(choice);
    // The fork of the whole flow: this is where full-vs-partial intent is first
    // declared, and the only place we learn WHICH line they wanted to drop. Fire
    // before anything else so it lands even if they abandon the chat next. The
    // product_choice is the chosen line's id, or "everything" for a full cancel.
    if (trackingShop) {
      trackBuyAgainWidgetInteract(
        "subscription_cancel_product_choice",
        trackingShop,
        {
          productId: product.id,
          subscription_contract_id: subscriptionId ?? undefined,
          product_choice:
            choice.kind === "single" ? choice.product.id : "everything",
          line_count: contractLines.length,
          ...cancelTrackProps(),
        },
      );
    }
    const subjectText =
      choice.kind === "single"
        ? buildCancelSubject(choice.product.title, []).text
        : buildCancelSubject(product.title, siblingProductTitles ?? []).text;
    const greetingChips =
      choice.kind === "single"
        ? getMotivationChips(choice.product.title, [])
        : getMotivationChips(product.title, siblingProductTitles ?? []);
    const productChoiceChips = buildProductChoices(contractLines).map(
      (c) => c.label,
    );
    // Keep the question + their pick in the transcript as their own bubbles, then
    // append the (now scoped) greeting below — rather than replacing everything with
    // just the greeting, which made the question they just answered disappear.
    setMessages([
      whichProductQuestionMessage(productChoiceChips),
      productChoiceAnswerMessage(answerLabel),
      greetingMessage({
        subjectText,
        firstName: customerName?.trim().split(/\s+/)[0] || null,
        replyOptions: greetingChips,
      }),
    ]);
  };

  const successCopy: Record<SuccessKind, { title: string; message: string }> = {
    cancelled: {
      title: "Subscription cancelled",
      message: "Your subscription has been cancelled.",
    },
    // Names the line going away AND what stays — the reassurance is the whole
    // point of the partial flow, so it can't be a generic "done" (see
    // cancel-chat-scope.ts). Computed from the scoped line at render.
    partial_cancelled: partialSuccessCopy({
      removedTitle: activeProduct.title,
      retainedTitles,
    }),
    swap: {
      title: "Request sent",
      message: "Our team will set up your product swap and email you shortly.",
    },
    switched: {
      title: "All set",
      message: "Your order has been switched to a one-time purchase.",
    },
    delayed: {
      title: "Delivery delayed",
      message: "We've pushed back your next delivery — nothing else to do.",
    },
    frequency_changed: {
      title: "Frequency updated",
      message: "Your deliveries will now arrive on your new schedule.",
    },
    bundle: {
      title: "Plan updated",
      message:
        "You're set up with the bigger size on your new schedule — same amount each month, less to pay.",
    },
    scheduled: {
      title: scheduledTitle(cancelContext.estimatedDiff),
      message: scheduledMessage(product.subscription_next_billing_date ?? ""),
    },
  };

  // Question + reply options for a single offer/confirm card.
  function cardContent(card: OfferCard): {
    bubble: ComponentChildren;
    options: ReplyOption[];
  } {
    const decline: ReplyOption = {
      label: "Not now",
      onClick: () => declineOffer(card),
    };

    if (card.kind === "switch_to_one_time") {
      const paid = cancelContext.paidAmount;
      const oneTime = cancelContext.oneTimeAmount;
      const diff = cancelContext.estimatedDiff ?? 0;
      // First presentation keeps the soft "Got it…" title; re-offers (after they
      // insist on cancelling) use the exit framing.
      const isFirstSwitch =
        cards.find((c) => c.kind === "switch_to_one_time")?.id === card.id;
      const title = isFirstSwitch
        ? SWITCH_TO_ONE_TIME_FIRST_TITLE
        : SWITCH_TO_ONE_TIME_TITLE;
      const nextDelivery = product.subscription_next_billing_date;
      const offerShown =
        cancelContext.cancelAfterNextDeliveryAvailable && !!nextDelivery;
      const priceLines = paid != null && oneTime != null && oneTime > 0 && (
        <div className="tw:mt-1 tw:flex tw:flex-col tw:gap-0.5">
          <p className="tw:m-0">
            <span className="tw:font-medium">You paid:</span> $
            {formatPrice(paid)}
          </p>
          <p className="tw:m-0">
            <span className="tw:font-medium">One-time price:</span> $
            {formatPrice(oneTime)}
          </p>
          <p className="tw:m-0 tw:mt-1">
            <span className="tw:font-medium">Difference today:</span> $
            {formatPrice(diff)}
          </p>
        </div>
      );
      return {
        bubble: (
          <div className="tw:flex tw:flex-col tw:gap-2">
            <p className="tw:m-0 tw:mb-4 tw:font-medium">
              {title}
            </p>
            <p className="tw:m-0">{SWITCH_TO_ONE_TIME_BODY}</p>
            {offerShown && nextDelivery ? (
              <>
                {/* Same order as the dialog: the offer first, then the price
                    lines that explain the "Cancel today" fee. */}
                <div className="tw:mt-2 tw:flex tw:flex-col tw:gap-2 tw:rounded-xl tw:bg-[#FDEDEE] tw:p-3">
                  <p className="tw:m-0 tw:font-semibold">
                    {waitHeading(nextDelivery)}
                  </p>
                  <ul className="tw:m-0 tw:p-0 tw:list-none tw:flex tw:flex-col tw:gap-1 tw:text-sm">
                    {[
                      noFeeLine(diff),
                      deliverySavingLine(diff, nextDelivery),
                      CANCEL_RIGHT_AFTER_LINE,
                    ].map((line) => (
                      <li key={line} className="tw:flex tw:items-start tw:gap-2">
                        <Check className="tw:size-4 tw:shrink-0 tw:mt-0.5" aria-hidden />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="tw:m-0 tw:pt-2 tw:border-t tw:border-[#523D33]/15 tw:font-semibold">
                    {totalSavingLine(diff)}
                  </p>
                </div>
                <div className="tw:mt-1 tw:flex tw:items-center tw:gap-3 tw:text-xs tw:uppercase tw:tracking-wide tw:text-[#523D33]/60">
                  <span className="tw:h-px tw:flex-1 tw:bg-[#523D33]/15" />
                  {OR_DIVIDER}
                  <span className="tw:h-px tw:flex-1 tw:bg-[#523D33]/15" />
                </div>
                {priceLines}
              </>
            ) : (
              priceLines
            )}
            <p className="tw:m-0 tw:mt-1 tw:text-xs tw:text-gray-500">
              {SWITCH_TO_ONE_TIME_FOOTER}
            </p>
          </div>
        ),
        options: cancelContext.cancelAfterNextDeliveryAvailable
          ? [
            {
              label: saveLabel(product.subscription_next_billing_date ?? ""),
              primary: true,
              pending,
              onClick: () =>
                void acceptOffer(
                  card,
                  rawSchedule,
                  "scheduled",
                  saveLabel(product.subscription_next_billing_date ?? ""),
                  { result: { cancelAction: "cancel_after_next_delivery" } },
                ),
            },
            {
              label: cancelTodayLabel(diff),
              onClick: () =>
                void acceptOffer(
                  card,
                  rawSwitch,
                  "switched",
                  cancelTodayLabel(diff),
                ),
            },
            {
              label: "Keep subscription",
              onClick: () =>
                declineOffer(card, { label: "Keep subscription" }),
            },
          ]
          : [
          {
            label:
              diff > 0
                ? `Switch to one-time (+$${formatPrice(diff)})`
                : "Switch to one-time",
            primary: true,
            pending,
            onClick: () =>
              void acceptOffer(
                card,
                rawSwitch,
                "switched",
                "Switch to one-time",
              ),
          },
          // Before the 2nd shipment there's no cancel — declining just keeps the
          // subscription as-is, so label it that way instead of "Not now".
          isBeforeSecondShipment
            ? {
              label: "Keep subscription",
              onClick: () =>
                declineOffer(card, { label: "Keep subscription" }),
            }
            : decline,
        ],
      };
    }

    if (card.kind === "delay") {
      // The model authors the option set; fall back to the standard presets
      // when it offered none (or none survived clamping).
      const choices = buildDelayChoices({
        weeks: card.proposedWeeks ?? null,
        presets: cancelContext.delayPresets,
        nextBillingDate: product.subscription_next_billing_date ?? null,
      });
      return {
        // This is no longer a rare fallback: when the model stalls, the watchdog
        // presents this card with NO message, so this ask is what the customer
        // actually reads. It has to stand on its own and name the action.
        bubble:
          card.message?.trim() ||
          `How about we delay your next delivery until you're ready for more?`,
        options: [
          // No `primary` here: 2 / 3 / 6 weeks are PEERS, not a recommendation and
          // two also-rans. Emphasising the first is arbitrary, reads as
          // pre-selected, and quietly nudges toward the shortest delay.
          ...choices.map((c) => ({
            label: c.label,
            pending: pending && submittingLabel === c.label,
            onClick: () =>
              void acceptOffer(
                card,
                () => rawDelay(c.date, c.weeks),
                "delayed",
                c.label,
              ),
          })),
          decline,
        ],
      };
    }

    if (card.kind === "change_frequency") {
      // The model authors the option set; fall back to the standard 2x/3x when
      // it offered none (or none survived clamping).
      const choices = buildFrequencyChoices({
        multipliers: card.proposedMultipliers ?? [2, 3],
        base: freqBase,
        subject,
        contractPerDeliveryTotal: cancelContext.contractPerDeliveryTotal,
        cadence: cancelContext.cadence,
      });
      return {
        // As with delay: with the watchdog no longer laundering the model's prose,
        // this ask is what a stalled turn actually shows the customer.
        bubble:
          card.message?.trim() ||
          `How about we space your deliveries out so they arrive less often?`,
        options: [
          // Peers, like the delay lengths above — no arbitrary favourite.
          ...choices.map((c) => ({
            label: c.label,
            sublabel: c.sublabel,
            pending: pending && submittingLabel === c.label,
            onClick: () =>
              void acceptOffer(
                card,
                () => rawChangeFreq(c.interval, c.unit),
                "frequency_changed",
                c.label,
              ),
          })),
          decline,
        ],
      };
    }

    if (card.kind === "bundle") {
      // The card owns EVERY figure (the model's message states none). If the
      // computation somehow fell through, degrade to declining — never show an
      // empty or figure-less bundle card.
      if (!bundleOffer) {
        return { bubble: card.message?.trim() || "", options: [decline] };
      }
      const copy = buildBundleCardCopy(bundleOffer, cancelContext.currencySymbol);

      // The offer is a single message. Accepting doesn't apply yet — it opens the
      // billing-date DIALOG (see the <Dialog> rendered in renderCard); only
      // confirming a date there applies the bundle.
      return {
        bubble: (
          <BundleOffer
            bundle={bundleOffer}
            currencySymbol={cancelContext.currencySymbol}
          />
        ),
        options: [
          {
            label: copy.acceptLabel,
            primary: true,
            pending,
            onClick: () => {
              // Pre-select their current next-billing date as the least-surprising
              // default, then open the date dialog.
              const win = bundleBillingDateWindow(
                product.subscription_next_billing_date ?? null,
              );
              setBundleBillingDate(win.initial);
              setBundleDatePickerFor(card.id);
            },
          },
          decline,
        ],
      };
    }

    if (card.kind === "swap") {
      const bundle = bundleOfCard(card);
      return {
        bubble: (
          <ProductSwapOffer
            currentProducts={swapCurrentProducts}
            currentPrice={currentPrice}
            candidates={bundle.products}
            comboValue={bundle.comboTotal}
            message={card.message}
          />
        ),
        options: [
          {
            label: "Swap my subscription",
            primary: true,
            pending,
            onClick: () =>
              void acceptOffer(
                card,
                () => rawSwap(card),
                "swap",
                "Swap my subscription",
              ),
          },
          decline,
        ],
      };
    }

    // confirm — but scoped to ONE line, this is a partial removal, not a
    // whole-contract cancel. The ask MUST say the other lines survive (they're on
    // a cancel screen and will assume "confirm" ends everything), and accepting it
    // hits the partial-cancel path — support removes just this line.
    if (isSingleLineScope) {
      return {
        bubble: partialConfirmQuestion({
          removedTitle: activeProduct.title,
          retainedTitles,
        }),
        options: [
          {
            label: `Cancel just ${activeProduct.title}`,
            primary: true,
            pending,
            onClick: () =>
              void acceptOffer(
                card,
                rawPartialCancel,
                "partial_cancelled",
                `Cancel just ${activeProduct.title}`,
              ),
          },
          { label: "Keep it", onClick: () => declineConfirm(card) },
        ],
      };
    }

    // confirm
    return {
      bubble: "Go ahead and cancel your subscription?",
      options: [
        {
          label: "Confirm cancellation",
          primary: true,
          pending,
          onClick: () =>
            void acceptOffer(
              card,
              rawConfirm,
              "cancelled",
              "Confirm cancellation",
            ),
        },
        { label: "Keep subscription", onClick: () => declineConfirm(card) },
      ],
    };
  }

  function renderCard(card: OfferCard): ComponentChildren {
    // Hide chips once they typed a voice reply (answer set) even while the
    // card stays status=pending; the UserBubble shows their text.
    const interactive =
      card.status === "pending" && !success && !isTyping && !card.answer;
    const applyingSpoken =
      card.status === "pending" &&
      !!card.answer &&
      pending &&
      submittingLabel != null;
    const { bubble, options } = cardContent(card);
    return (
      <div key={card.id} className="tw:flex tw:flex-col tw:gap-3">
        <AssistantBubble variant={card.kind === "bundle" ? "card" : "gray"}>
          {bubble}
        </AssistantBubble>
        {interactive ? (
          <ReplyOptions options={options} disabled={pending} />
        ) : card.answer ? (
          <>
            <UserBubble>{card.answer}</UserBubble>
            {applyingSpoken ? (
              <div className="tw:flex tw:items-center tw:gap-2 tw:pl-1">
                <TypingIndicator />
                <span className="tw:text-xs tw:text-[#523D33]/70">
                  Applying {submittingLabel}…
                </span>
              </div>
            ) : null}
          </>
        ) : null}
        {card.kind === "bundle" && bundleOffer && (
          <BundleBillingDateDialog
            open={bundleDatePickerFor === card.id}
            nextBillingDate={product.subscription_next_billing_date ?? null}
            value={bundleBillingDate}
            pending={pending}
            onSelect={(ymd) => setBundleBillingDate(ymd)}
            onConfirm={(chosen) => {
              setBundleDatePickerFor(null);
              void acceptOffer(
                card,
                () => rawBundle(bundleOffer, chosen),
                "bundle",
                `Bill me on ${formatSubscriptionDateForDisplay(chosen)}`,
              );
            }}
            onCancel={() => {
              setBundleDatePickerFor(null);
              setBundleBillingDate(null);
            }}
          />
        )}
      </div>
    );
  }

  // The transcript: walk the message stream in order, rendering text bubbles and
  // — inline, at the position of their tool call — the offer/confirm cards.
  function renderTranscript(): ComponentChildren {
    const rows: ComponentChildren[] = [];
    // The assistant message currently streaming in (if any). In the save phase its
    // text is withheld until it finalizes: offers often stream a lead-in sentence
    // before the tool call arrives, and showing it would flash-then-vanish once the
    // card replaces it. The typing indicator covers the wait; once the message is
    // complete, presentsCard decides (card only, or a plain closing line).
    const streamingId =
      isTyping && messages.length > 0
        ? messages[messages.length - 1]?.role === "assistant"
          ? messages[messages.length - 1]?.id
          : null
        : null;
    // When the watchdog recovers a statement-only stall with a synthetic card,
    // that card replaces the dead-end sentence (same as a model offer_* turn).
    for (const m of messages) {
      if (m.role !== "user" && m.role !== "assistant") continue;
      const parts = (m.parts ?? []) as Array<{
        type: string;
        text?: string;
        toolCallId?: string;
        input?: unknown;
      }>;
      // When an assistant message ALSO presents an offer/confirm card, the card is
      // the whole message — suppress any lead-in text the model added before it so
      // the offer is a single bubble (the model is told not to, but can't be relied
      // on). Intake and plain replies keep their text.
      const presentsCard = parts.some(
        (p) =>
          p.type.startsWith("tool-offer_") || p.type === "tool-confirm_cancel",
      );
      // Synthetic recovery cards are keyed to the stalled message id
      // (`synthetic-offer-${kind}-${m.id}` / `synthetic-confirm-${m.id}`),
      // or to a reoffer index when switch is the only lever left.
      // Hide that dead-end sentence — the card is the only message.
      const recoveredBySynthetic = cards.some(
        (c) =>
          c.synthetic &&
          (c.id === `synthetic-confirm-${m.id}` ||
            (c.id.startsWith("synthetic-offer-") && c.id.endsWith(`-${m.id}`))),
      );
      const hasInteractiveSyntheticSwitch = cards.some(
        (c) =>
          c.synthetic &&
          c.kind === "switch_to_one_time" &&
          c.status === "pending" &&
          !c.answer,
      );
      const isLastAssistant =
        m.role === "assistant" &&
        m.id ===
        [...messages].reverse().find((x) => x.role === "assistant")?.id;
      const hasVisibleText = parts.some(
        (p) => p.type === "text" && p.text?.trim(),
      );
      parts.forEach((part, idx) => {
        if (part.type === "text") {
          if (presentsCard || recoveredBySynthetic) return;
          const trimmedText = (part.text ?? "").trim();
          // Switch-only re-offer: hide soft-close / text-ask above the card.
          if (hasInteractiveSyntheticSwitch && isLastAssistant && trimmedText) {
            return;
          }
          // Save-phase: hold the still-streaming message's text to avoid the
          // lead-in-then-card flash (see streamingId). Intake streams normally.
          if (reasonSet && m.role === "assistant" && m.id === streamingId)
            return;
          const text = part.text ?? "";
          if (!text.trim()) return;
          rows.push(
            m.role === "user" ? (
              <UserBubble key={`${m.id}-t${idx}`}>{text}</UserBubble>
            ) : (
              <AssistantBubble key={`${m.id}-t${idx}`}>{text}</AssistantBubble>
            ),
          );
        } else if (
          part.type === "tool-suggest_replies" &&
          m.role === "assistant" &&
          !hasVisibleText
        ) {
          // Some models emit the chips tool call with no text part at all —
          // without this the customer sees answers to an invisible question.
          // The tool's required `question` input becomes the bubble.
          const question = (part.input as { question?: string } | undefined)
            ?.question;
          if (question?.trim()) {
            rows.push(
              <AssistantBubble key={`${m.id}-q${idx}`}>
                {question}
              </AssistantBubble>,
            );
          }
        } else if (part.type.startsWith("tool-") && part.toolCallId) {
          const card = cards.find((c) => c.id === part.toolCallId);
          if (card) rows.push(renderCard(card));
        }
      });
    }
    // Synthetics without an injected tool part still append last (legacy path).
    // Injected synthetics already rendered above via their toolCallId.
    const stampedSyntheticIds = new Set(
      messages.flatMap((m) =>
        (m.parts ?? [])
          .map((p) => (p as { toolCallId?: string }).toolCallId)
          .filter((id): id is string => typeof id === "string"),
      ),
    );
    for (const card of cards.filter((c) => c.synthetic)) {
      if (stampedSyntheticIds.has(card.id)) continue;
      rows.push(renderCard(card));
    }
    return <>{rows}</>;
  }

  const successCard: ComponentChildren = success ? (
    <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-6 tw:px-4 tw:text-center tw:animate-in tw:fade-in tw:zoom-in-95 tw:duration-300">
      <div className="tw:rounded-full tw:bg-[#FDEDEE] tw:p-3 tw:mb-3">
        <CircleCheck
          className="tw:size-10 tw:text-[#523D33]"
          strokeWidth={1.5}
        />
      </div>
      <p
        className="tw:text-lg tw:font-semibold tw:m-0 tw:mb-1"
        style={{ color: "var(--so-text, #523D33)" }}
      >
        {success === "swap" && swapCompletedRef.current
          ? "Products updated"
          : successCopy[success].title}
      </p>
      <p className="tw:text-sm tw:m-0 tw:text-gray-500 tw:max-w-65">
        {success === "swap" && swapCompletedRef.current
          ? "Your subscription now includes your new products."
          : successCopy[success].message}
      </p>
      <Button
        type="button"
        onClick={() => onOpenChange(false)}
        className="tw:mt-5 tw:rounded-full tw:bg-[#523D33] tw:text-white tw:px-6 hover:tw:bg-[#523D33]/90"
      >
        OK
      </Button>
    </div>
  ) : null;

  // Recovery card for a fatal error in single-line scope. It deliberately offers
  // NO path that could cancel the whole contract — only a way out (close/reopen
  // the manage sheet) and the reassurance that the chosen line is untouched, so a
  // flaky turn never turns a "drop one product" into "cancel everything". Names
  // the line so they know exactly what still needs removing when they contact us.
  const singleLineErrorCard: ComponentChildren = singleLineFatalError ? (
    <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-6 tw:px-4 tw:text-center tw:animate-in tw:fade-in tw:zoom-in-95 tw:duration-300">
      <div className="tw:rounded-full tw:bg-[#FDEDEE] tw:p-3 tw:mb-3">
        <CircleAlert
          className="tw:size-10 tw:text-[#523D33]"
          strokeWidth={1.5}
        />
      </div>
      <p
        className="tw:text-lg tw:font-semibold tw:m-0 tw:mb-1"
        style={{ color: "var(--so-text, #523D33)" }}
      >
        Something went wrong
      </p>
      <p className="tw:text-sm tw:m-0 tw:text-gray-500 tw:max-w-65">
        {`We couldn't finish removing ${activeProduct.title}. Please contact our support team and we'll take it off for you — the rest of your subscription is untouched.`}
      </p>
      <Button
        type="button"
        onClick={() => onOpenChange(false)}
        className="tw:mt-5 tw:rounded-full tw:bg-[#523D33] tw:text-white tw:px-6 hover:tw:bg-[#523D33]/90"
      >
        OK
      </Button>
    </div>
  ) : null;

  // No composer while the which-product question is up: there is nothing to type,
  // they must pick a chip (or "Cancel everything"). It returns the moment they do.
  // Also suppressed on the single-line error card — that state has no chat to
  // continue, only a way out.
  const showInput = !success && !needsProductChoice && !singleLineFatalError;

  // The which-product question — app-rendered, deterministic, and the FIRST thing
  // a 2+ line contract shows (before any model call). One chip per line, then
  // "Cancel everything"; picking re-scopes the conversation (chooseProduct).
  const productChoices = needsProductChoice
    ? buildProductChoices(contractLines)
    : [];
  const productChoiceOptions: ReplyOption[] = productChoices.map((choice) => ({
    label: choice.label,
    onClick: () =>
      chooseProduct(
        choice.value === CANCEL_EVERYTHING
          ? { kind: "everything" }
          : {
            kind: "single",
            product:
              contractLines.find((l) => l.id === choice.value) ?? product,
          },
        choice.label,
      ),
  }));

  // Tappable answers during intake — two sources only:
  //
  //  - The OPENING turn ("what drew you to X to begin with?") is the app's own
  //    greeting — no model call yet. Chips come from the deterministic per-product
  //    motivation table (cancel-chat-motivations.ts).
  //  - After that, ONLY chips from the model's suggest_replies on the latest
  //    assistant turn (cancel-reason catalog labels, dig-deep probes, recovery).
  //    No silent catalog fallback — that painted why-cancel chips under mismatched
  //    questions when the model forgot the tool.
  //
  // Chips never block typing: they only save the customer from it. Tapping a
  // reason sends it exactly as if they had written it — except "Other", which
  // focuses the composer so the mobile keyboard opens for free text.
  const lastMessage = messages[messages.length - 1];
  const lastAssistantParts =
    lastMessage?.role === "assistant"
      ? ((lastMessage.parts ?? []) as Array<{
        type: string;
        text?: string;
        input?: unknown;
      }>)
      : [];
  const aiSuggestions = (
    ((
      lastAssistantParts.find((p) => p.type === "tool-suggest_replies")
        ?.input as { options?: unknown } | undefined
    )?.options ?? []) as unknown[]
  ).filter((s): s is string => typeof s === "string" && s.trim().length > 0);
  const userTurnCount = messages.filter(isIntakeUserTurn).length;
  // Turn 0 is the app's own greeting ("what drew you to X to begin with?"). The
  // model hasn't been called yet, so it cannot have authored chips for it —
  // these come from the deterministic per-product table instead, which is what
  // keeps the opener instant.
  const isOpeningTurn = userTurnCount === 0;
  const showIntakeSuggestions =
    !success &&
    !reasonSet &&
    !isTyping &&
    !pending &&
    // The which-product question owns the turn until they pick; its chips are the
    // product choices, not motivations. Only after the pick does intake begin.
    !needsProductChoice &&
    (isOpeningTurn || aiSuggestions.length > 0);
  const suggestionItems: { id?: string; label: string }[] = isOpeningTurn
    ? // Scoped to the line they picked (activeProduct): a single-line greeting
    // asks about ONE product, so its motivation chips must be that product's.
    getMotivationChips(
      activeProduct.title,
      isSingleLineScope ? [] : siblingProductTitles ?? [],
    ).map((label) => ({ label }))
    : aiSuggestions.map((label) => {
      const reason = cancelContext.allowedReasons.find((r) => r.label === label);
      return reason ? { id: reason.id, label } : { label };
    });
  const suggestionLabels = suggestionItems.map((item) => item.label);
  const intakeSuggestions: ReplyOption[] = showIntakeSuggestions
    ? suggestionItems.slice(0, 5).map((item, i) => ({
      label: item.label,
      // Tapping still sends the label exactly as if they had written it — the
      // model sees no difference. The origin is for US: it is the only place that
      // knows this was a tap, and once the text is in the transcript that fact is
      // gone forever. See cancel-chat-intake.ts.
      // "Other" is the exception: it means "I'll type it" — focus the composer
      // (opens the mobile keyboard) instead of sending the word "Other".
      onClick: () => {
        if (item.id === "other" || item.label === "Other") {
          composerInputRef.current?.focus();
          return;
        }
        send(item.label, { source: "chip", chipIndex: i });
      },
    }))
    : [];

  // Remember what was on offer for the opening question, and count the impression
  // once — without a denominator the tap count says nothing about tap RATE.
  const openingChips = isOpeningTurn ? suggestionLabels.slice(0, 5) : null;
  useEffect(() => {
    if (!open || !showIntakeSuggestions || openingChips == null) return;
    openingChipsRef.current = openingChips;
    if (motivationShownRef.current) return;
    motivationShownRef.current = true;
    if (trackingShop) {
      trackBuyAgainWidgetInteract(
        "subscription_cancel_motivation_shown",
        trackingShop,
        {
          productId: product.id,
          subscription_contract_id: subscriptionId ?? undefined,
          motivation_chips_shown: openingChips.length,
          ...cancelTrackProps(),
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per conversation
  }, [open, showIntakeSuggestions, openingChips == null]);

  const innerBody: ComponentChildren = (
    <>
      <div
        ref={scrollRef}
        className="tw:flex-1 tw:overflow-y-auto tw:overscroll-contain tw:min-h-0 tw:px-4 tw:py-3"
      >
        <div className="tw:flex tw:flex-col tw:gap-3">
          {singleLineFatalError ? (
            singleLineErrorCard
          ) : (
            <>
              {needsProductChoice && (
                <>
                  <AssistantBubble>{WHICH_PRODUCT_QUESTION}</AssistantBubble>
                  <ReplyOptions
                    options={productChoiceOptions}
                    disabled={pending}
                  />
                </>
              )}
              {renderTranscript()}
              {chatBusy && (
                <div className="tw:flex tw:justify-start">
                  <TypingIndicator />
                </div>
              )}
              {intakeSuggestions.length > 0 && (
                <ReplyOptions options={intakeSuggestions} disabled={pending} />
              )}
              {successCard}
            </>
          )}
        </div>
      </div>
      {showInput && (
        <div className="tw:shrink-0 tw:px-4 tw:pb-4 tw:pt-2 tw:bg-background">
          <MessageInput
            value={input}
            onChange={setInput}
            onSubmit={() => send(input)}
            disabled={chatBusy || pending}
            placeholder="Write a reply…"
            inputRef={composerInputRef}
          />
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Mobile: fullscreen sheet, edge to edge, so the store page never shows
          through behind the conversation. While the keyboard is up the sheet is
          pinned to the EXACT visible viewport — but NOT from here. Tailwind runs
          with `important: true` in this package, so `h-full` below is
          `height: 100% !important` and a plain inline style cannot beat it. The
          pinning is applied imperatively with `!important` priority instead; see
          the applyKeyboardGeometry effect above. Do not "simplify" it back into a
          `style` prop — that is the bug this had for months. */}
      <Sheet open={open && !isDesktop} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          showCloseButton={false}
          onOpenChange={onOpenChange}
          overlayClassName="tw:z-70"
          className="tw:data-[side=bottom]:inset-0 tw:data-[side=bottom]:h-full tw:flex tw:flex-col tw:gap-0 tw:rounded-none tw:overflow-hidden tw:z-71 tw:data-[side=bottom]:z-71"
        >
          <SheetHeader className="tw:shrink-0 tw:flex tw:flex-row tw:items-center tw:justify-between tw:pr-2 tw:pb-2">
            <SheetTitle className="tw:font-semibold tw:text-base tw:text-[#523D33]">
              Cancel
            </SheetTitle>
            <SheetClose
              data-slot="sheet-close"
              render={
                <Button
                  variant="ghost"
                  className="tw:p-0 tw:m-0"
                  size="icon-sm"
                  aria-label="Close"
                />
              }
            >
              <X />
            </SheetClose>
          </SheetHeader>
          {innerBody}
        </SheetContent>
      </Sheet>

      {/* Desktop: centered dialog */}
      <Dialog open={open && isDesktop} onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton={false}
          onOpenChange={onOpenChange}
          overlayClassName="tw:z-70 tw:bg-black/60 tw:supports-backdrop-filter:backdrop-blur-sm"
          className="tw:z-71 tw:p-0 tw:sm:max-w-xl tw:min-h-160 tw:max-h-[90dvh] tw:flex tw:flex-col tw:overflow-hidden"
        >
          <DialogHeader className="tw:shrink-0 tw:px-4 tw:pt-4">
            <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:w-full">
              <DialogTitle className="tw:min-w-0 tw:flex-1 tw:pr-1 tw:text-left tw:font-semibold tw:text-base tw:text-[#523D33]">
                Cancel
              </DialogTitle>
              <DialogClose
                data-slot="dialog-close"
                render={
                  <Button
                    variant="ghost"
                    className="tw:p-0 tw:m-0 tw:shrink-0"
                    size="icon-sm"
                    aria-label="Close"
                  />
                }
              >
                <X />
              </DialogClose>
            </div>
          </DialogHeader>
          {innerBody}
        </DialogContent>
      </Dialog>
    </>
  );
}
